import React from "react";
import { Icon, EmptyState, notify, confirmDialog, useLocalPref, useNarrowScreen, ListRow } from "./registry";
import { csvRecords } from "./lib/csv";
import { QrCode } from "./lib/qr";
import { OutreachPanel, useOutreachToday, LoCallCard, LoReplyCard, LoCallScript, loPitchSummary } from "./leadoutreach";   // phase 4: outreach
/* ============================================================
   Fence Works — FW Admin → Lead Engine (platform owner only)
   docs/LEAD_ENGINE_PLAN.md §3 (screens) and §14 (the build contract).

   Pipeline          every prospect: stat row, stage chips, search, source /
                     state filters, sort; add one, paste a link, import a CSV,
                     or go find some on Google.
   Prospect          one business: score and stage, their website audit with
                     screenshots, what we read off their site, the preview site
                     we built them (link, QR, views, expiry), the timeline, notes,
                     tags, next step, and one-tap call / note / DM logging.
   Find prospects    a Google search ("fence contractor in Green Bay, WI"),
                     tick the ones you want, add them to the pipeline.
   Today             who's looking at their preview right now, then the calls
                     due today, each with one-tap outcomes.

   Everything goes through window.VFP_DB.leadengine → the service's
   /leadengine routes, owner-gated there (SUPPORT_OWNER_EMAILS, verified).
   Anything scraped from a prospect's website is shown as text or as an
   https image only — never as HTML, never as a javascript: link.
   ============================================================ */

/* ---------------- pure helpers (scripts/unit.mjs tests these) ---------------- */
const LE_STAGES: [string, string, string][] = [
  ["new", "New", "var(--cyan)"], ["qualified", "Qualified", "var(--teal)"], ["site_built", "Site built", "var(--violet)"],
  ["contacted", "Contacted", "var(--amber)"], ["engaged", "Engaged", "#f0a35e"], ["demo", "Demo booked", "var(--violet)"],
  ["trial", "Trial", "var(--violet)"], ["customer", "Customer", "var(--green)"], ["lost", "Lost", "var(--red)"], ["not_fit", "Not a fit", "var(--fg-3)"],
];
const LE_SOURCES: Record<string, string> = { manual: "Added by hand", csv: "CSV import", places: "Google search", link: "Pasted link", inbound: "Inbound form", stalled_signup: "Stalled signup" };
const LE_OUTCOMES: [string, string][] = [["connected", "Connected"], ["voicemail", "Voicemail"], ["no_answer", "No answer"], ["not_interested", "Not interested"], ["call_back", "Call back"]];
/* the import's column targets. The "(append)" ones take any number of
   columns: each value becomes a tag, or a "Column: value" line in the notes. */
const LE_FIELDS: [string, string][] = [
  ["business_name", "Business name"], ["website", "Website"], ["phone", "Phone"], ["email", "Email"],
  ["first_name", "First name"], ["last_name", "Last name"], ["owner_name", "Contact (full name)"],
  ["street", "Street"], ["city", "City"], ["state", "State"], ["zip", "ZIP"],
  ["notes", "Notes"], ["notes_append", "Notes (append)"], ["tags", "Tags"], ["tags_append", "Tags (append)"],
  ["external_id", "External ID"], ["place_id", "Google place ID"],
];
const LE_MULTI = new Set(["notes_append", "tags_append"]);
/* header synonyms, best first — "Company" beats "Name" for the business */
const LE_SYNONYMS: Record<string, string[]> = {
  business_name: ["businessname", "companyname", "company", "business", "dba", "doingbusinessas", "organization", "organisation", "accountname", "account", "contractor", "contractorname", "firm", "firmname", "legalname", "licensee", "licenseename", "entityname", "membername", "name"],
  website: ["website", "websiteurl", "web", "webaddress", "url", "homepage", "site", "domain", "www"],
  phone: ["phone", "phonenumber", "businessphone", "mainphone", "officephone", "primaryphone", "telephone", "tel", "workphone", "contactphone", "mobile", "cell", "cellphone", "phone1"],
  email: ["email", "emailaddress", "businessemail", "contactemail", "primaryemail", "mail", "email1"],
  first_name: ["firstname", "first", "fname", "givenname", "contactfirstname", "ownerfirstname"],
  last_name: ["lastname", "last", "lname", "surname", "familyname", "contactlastname", "ownerlastname"],
  owner_name: ["ownername", "owner", "contactname", "contact", "fullname", "contactfullname", "contactperson", "principal", "qualifier"],
  street: ["street", "streetaddress", "address", "address1", "addressline1", "mailingaddress", "physicaladdress", "businessaddress"],
  city: ["city", "town", "mailingcity", "municipality"],
  state: ["state", "st", "statecode", "stateabbr", "mailingstate", "province", "stateprovince", "region", "territory"],
  zip: ["zip", "zipcode", "postalcode", "postal", "postcode", "zip5", "mailingzip"],
  notes: ["notes", "note", "comments", "comment", "description", "remarks"],
  tags: ["tags", "tag", "labels", "label", "category", "categories", "segment"],
  external_id: ["leadid", "externalid", "recordid", "id", "extid", "sourceid", "crmid", "uniqueid", "listid"],
  place_id: ["placeid", "googleplaceid"],
};
/* headers that only mean State when the column actually holds states — a
   "Region: Panhandle" column is a region, not a state */
const LE_STATE_MAYBE = new Set(["province", "stateprovince", "region", "territory"]);
const LE_US_STATES: [string, string][] = [
  ["AL", "Alabama"], ["AK", "Alaska"], ["AZ", "Arizona"], ["AR", "Arkansas"], ["CA", "California"], ["CO", "Colorado"], ["CT", "Connecticut"], ["DE", "Delaware"],
  ["DC", "District of Columbia"], ["FL", "Florida"], ["GA", "Georgia"], ["HI", "Hawaii"], ["ID", "Idaho"], ["IL", "Illinois"], ["IN", "Indiana"], ["IA", "Iowa"],
  ["KS", "Kansas"], ["KY", "Kentucky"], ["LA", "Louisiana"], ["ME", "Maine"], ["MD", "Maryland"], ["MA", "Massachusetts"], ["MI", "Michigan"], ["MN", "Minnesota"],
  ["MS", "Mississippi"], ["MO", "Missouri"], ["MT", "Montana"], ["NE", "Nebraska"], ["NV", "Nevada"], ["NH", "New Hampshire"], ["NJ", "New Jersey"], ["NM", "New Mexico"],
  ["NY", "New York"], ["NC", "North Carolina"], ["ND", "North Dakota"], ["OH", "Ohio"], ["OK", "Oklahoma"], ["OR", "Oregon"], ["PA", "Pennsylvania"], ["RI", "Rhode Island"],
  ["SC", "South Carolina"], ["SD", "South Dakota"], ["TN", "Tennessee"], ["TX", "Texas"], ["UT", "Utah"], ["VT", "Vermont"], ["VA", "Virginia"], ["WA", "Washington"],
  ["WV", "West Virginia"], ["WI", "Wisconsin"], ["WY", "Wyoming"],
];
const LE_EVENT_ICON: Record<string, string> = {
  created: "plus", imported: "upload", merged: "layers", enriched: "search", enrich_failed: "x", site_built: "building", site_extended: "clock",
  site_deleted: "trash", preview_viewed: "activity", claimed: "check", signed_up: "user", stage_changed: "arrow", note: "pen", call: "phone",
  email: "mail", dm: "chat", inbound: "bell", do_not_contact: "lock", replied: "chat", unsubscribed: "x", bounced: "x",
  outreach: "send", email_sent: "mail", email_opened: "mail", clicked: "arrow", call_task: "phone", complained: "x",
};
const LE_KIND_LABEL: Record<string, string> = { scorecard: "Fence Ops Scorecard", demo: "demo request", signup: "signup form", founder: "founding-member application", contact: "contact form", coverage: "coverage request", waitlist: "waitlist", quote: "quote request",
  site_preview: "Website preview tool", profile_score: "Google Profile score" };
/* a prospect's source detail in words: an inbound one names the form or tool it came from ("Website preview tool"); anything else as typed */
function leSourceDetail(p: any): string {
  const d = String((p && p.source_detail) || "");
  return p && p.source === "inbound" ? LE_KIND_LABEL[d] || d : d;
}

function leNormHeader(h: any): string {
  return String(h == null ? "" : h).toLowerCase().replace(/[^a-z0-9]/g, "");
}

/* "Florida" / "fl" / "F.L." → "FL"; anything that isn't a US state (or DC) → ""
   (the server's normalizeState, so the modal and the import agree) */
function leNormState(v: any): string {
  const t = String(v == null ? "" : v).replace(/\./g, "").replace(/\s+/g, " ").trim();
  if (!t) return "";
  if (/^[a-z]{2}$/i.test(t)) { const up = t.toUpperCase(); return LE_US_STATES.some((s) => s[0] === up) ? up : ""; }
  const hit = LE_US_STATES.find((s) => s[1].toLowerCase() === t.toLowerCase());
  return hit ? hit[0] : "";
}

/* the dedupe spelling of a business name (the server's normalizeBusinessName):
   "The Martin's Seamless Gutters, LLC" → "martins seamless gutters" */
function leNormBusinessName(v: any): string {
  const s = String(v == null ? "" : v).normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase()
    .replace(/['’‘`´.]/g, "").replace(/&/g, " and ").replace(/[^a-z0-9]+/g, " ").trim();
  let w = s ? s.split(" ") : [];
  if (w.length > 1 && w[0] === "the") w = w.slice(1);
  const suffix = ["llc", "inc", "incorporated", "co", "company", "corp", "corporation", "ltd", "limited", "pllc", "llp"];
  while (w.length > 1 && suffix.includes(w[w.length - 1])) w.pop();
  return w.join(" ");
}

/* a full name → first + last, split at the first space (as the server and #26's backfill do) */
function leSplitName(full: any): { first: string; last: string } {
  const s = String(full == null ? "" : full).replace(/\s+/g, " ").trim();
  const i = s.indexOf(" ");
  return i < 0 ? { first: s, last: "" } : { first: s.slice(0, i), last: s.slice(i + 1) };
}
/* who to ask for: first + last when we have them, else the full name */
function leContactName(p: any): string {
  if (!p) return "";
  const fl = [p.contact_first_name, p.contact_last_name].map((x: any) => String(x == null ? "" : x).trim()).filter(Boolean).join(" ");
  return fl || String(p.owner_name || "").trim();
}

/* CSV headers → prospect fields ("" = skip). Exact synonyms first, best
   synonym wins; then a looser "contains" pass; a leftover "Name" beside a
   company column is the contact. `samples` (a few values per column) keep a
   Region / Territory column out of State unless it really holds states. */
function leAutoMap(headers: string[], samples?: Record<string, any[]>): Record<string, string> {
  const out: Record<string, string> = {};
  const norm = headers.map(leNormHeader);
  const taken = new Set<string>();
  const holdsStates = (h: string) => {
    const vs = ((samples && samples[h]) || []).map((v) => String(v == null ? "" : v).trim()).filter(Boolean);
    return vs.length > 0 && vs.filter((v) => leNormState(v)).length / vs.length >= 0.8;
  };
  const fits = (h: string, i: number, field: string) => field !== "state" || !LE_STATE_MAYBE.has(norm[i]) || holdsStates(h);
  for (const [field] of LE_FIELDS) {
    for (const syn of LE_SYNONYMS[field] || []) {
      const i = norm.findIndex((n, k) => n === syn && !out[headers[k]] && fits(headers[k], k, field));
      if (i >= 0) { out[headers[i]] = field; taken.add(field); break; }
    }
  }
  const split = () => taken.has("first_name") || taken.has("last_name");
  headers.forEach((h, i) => {
    if (!out[h] && norm[i] === "name" && taken.has("business_name") && !taken.has("owner_name") && !split()) { out[h] = "owner_name"; taken.add("owner_name"); }
  });
  const loose: [RegExp, string][] = [
    [/company|business|dba|organi[sz]ation/, "business_name"], [/email|e-?mail/, "email"], [/phone|tel|mobile|cell/, "phone"],
    [/^(?!.*(map|google|facebook|instagram|linkedin|yelp|twitter|source|referr)).*(website|url|domain)/, "website"],
    [/^(?!.*(role|title|type|status|method|pref)).*(owner|contact)/, "owner_name"],
    [/zip|postal/, "zip"], [/street|address/, "street"], [/city|town/, "city"], [/^(?!.*(region|territor)).*(state|province)/, "state"],
    [/note|comment/, "notes"], [/tag|categor/, "tags"], [/segment|tier$/, "tags_append"],
  ];
  headers.forEach((h, i) => {
    if (out[h]) return;
    for (const [re, field] of loose) {
      if ((taken.has(field) && !LE_MULTI.has(field)) || !re.test(norm[i]) || !fits(h, i, field)) continue;
      if (field === "owner_name" && split()) continue;   // first + last already say who it is
      out[h] = field; taken.add(field); return;
    }
  });
  headers.forEach((h) => { if (!out[h]) out[h] = ""; });
  return out;
}

/* CSV records + the mapping → rows for POST /import (empty rows dropped).
   First / Last name → contact_first_name / contact_last_name; several columns
   on one field are joined; Tags splits on commas while Tags (append) keeps a
   cell whole as one tag; Notes (append) becomes "Column: value" lines after
   the row's own notes. */
function leRowsFromMapping(records: Record<string, any>[], mapping: Record<string, string>): Record<string, any>[] {
  const pairs = Object.keys(mapping).filter((h) => mapping[h]).map((h) => [h, mapping[h]]);
  const key: Record<string, string> = { first_name: "contact_first_name", last_name: "contact_last_name" };
  return (records || []).map((r) => {
    const o: Record<string, any> = {};
    const tags: string[] = [], notes: string[] = [], lines: string[] = [];
    for (const [h, f] of pairs) {
      const v = String(r[h] == null ? "" : r[h]).trim();
      if (!v) continue;
      if (f === "tags") tags.push(...v.split(/[,;|]/).map((s) => s.trim()).filter(Boolean));
      else if (f === "tags_append") tags.push(v);
      else if (f === "notes") notes.push(v);
      else if (f === "notes_append") lines.push(h + ": " + v);
      else { const k = key[f] || f; o[k] = o[k] ? o[k] + " " + v : v; }
    }
    if (tags.length) o.tags = tags;
    if (notes.length || lines.length) o.notes = [...notes, ...lines].join("\n");
    return o;
  }).filter((o) => Object.keys(o).length > 0);
}

/* the dry run → the line above the Import button:
   "169 rows: 142 new · 25 already in your pipeline (blanks filled) · 2 you deleted before (skipped)" */
function leDrySummary(d: any, includeDeleted?: boolean): string {
  if (!d) return "";
  const n = (x: any) => Number(x || 0).toLocaleString("en-US");
  const bits = [n(d.willCreate) + " new"];
  if (d.willMatch) bits.push(n(d.willMatch) + " already in your pipeline (blanks filled)");
  if (d.inFile) bits.push(n(d.inFile) + " repeat" + (d.inFile === 1 ? "s" : "") + " another row (merged)");
  if (d.deleted) bits.push(n(d.deleted) + " you deleted before (" + (includeDeleted ? "coming back" : "skipped") + ")");
  if (d.invalid) bits.push(n(d.invalid) + " without a business name (skipped)");
  return n(d.rows) + " row" + (d.rows === 1 ? "" : "s") + ": " + bits.join(" · ");
}

function leChunks(list: any[], size: number): any[][] {
  const out: any[][] = [];
  for (let i = 0; i < (list || []).length; i += size) out.push(list.slice(i, i + size));
  return out;
}

/* opportunity score → band + color (plan §5: higher = better to pitch) */
function leScoreBand(score: any): { band: string; color: string; label: string } {
  if (score == null || score === "" || !Number.isFinite(Number(score))) return { band: "none", color: "var(--fg-faint)", label: "Not scored yet" };
  const s = Number(score);
  if (s >= 70) return { band: "hot", color: "var(--green)", label: "Hot" };
  if (s >= 50) return { band: "warm", color: "var(--teal)", label: "Warm" };
  if (s >= 30) return { band: "mild", color: "var(--amber)", label: "Lukewarm" };
  return { band: "cold", color: "var(--fg-3)", label: "Cold" };
}

/* "just now" · "4 min ago" · "3h ago" · "3d ago" · "2mo ago" · "in 2d" · "never" */
function leTimeAgo(v: any, now: number = Date.now()): string {
  const t = v == null || v === "" ? NaN : typeof v === "number" ? v : Date.parse(v);
  if (!Number.isFinite(t)) return "never";
  const s = (now - t) / 1000;
  const a = Math.abs(s);
  if (a < 60) return "just now";
  const txt = a < 3600 ? Math.round(a / 60) + " min" : a < 86400 ? Math.round(a / 3600) + "h" : a < 86400 * 45 ? Math.round(a / 86400) + "d" : Math.round(a / (86400 * 30)) + "mo";
  return s < 0 ? "in " + txt : txt + " ago";
}

function leOverdue(v: any, now: number = Date.now()): boolean {
  const t = v == null || v === "" ? NaN : Date.parse(v);
  return Number.isFinite(t) && t < now;
}

/* rows → { all, new, qualified, … } */
function leStageCounts(rows: any[]): Record<string, number> {
  const out: Record<string, number> = { all: 0 };
  for (const r of rows || []) { out.all++; out[r.stage] = (out[r.stage] || 0) + 1; }
  return out;
}

function leStageLabel(stage: string): string {
  const s = LE_STAGES.find((x) => x[0] === stage);
  return s ? s[1] : String(stage || "").replace(/_/g, " ");
}

/* the score's breakdown (B writes it: an object of points, or a list) → lines */
function leBreakdownLines(b: any): string[] {
  const human = (k: string) => String(k).replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
  const pts = (v: any) => (typeof v === "number" ? (v > 0 ? " +" : " ") + v : "");
  if (!b) return [];
  if (Array.isArray(b)) return b.map((x) => (x && typeof x === "object" ? String(x.label || x.reason || x.key || "") + pts(x.points ?? x.pts ?? x.value) : String(x))).filter((s) => s.trim());
  if (typeof b !== "object") return [];
  return Object.keys(b).filter((k) => b[k] != null && b[k] !== 0 && b[k] !== false && k !== "total").map((k) => {
    const v = b[k];
    if (typeof v === "number") return human(k) + pts(v);
    if (v && typeof v === "object") return String(v.label || human(k)) + pts(v.points ?? v.pts);
    return human(k) + ": " + String(v);
  });
}

/* the Find box: "Green Bay, WI" is a place; anything else is a search */
function leParseFindQuery(s: any): { query?: string; city?: string; state?: string } {
  const t = String(s == null ? "" : s).trim().replace(/\s+/g, " ");
  if (!t) return {};
  const m = /^([A-Za-z .'-]+),\s*([A-Za-z]{2})$/.exec(t);
  if (m && !/fence|contractor|install|company|builder/i.test(m[1])) return { city: m[1].trim(), state: m[2].toUpperCase() };
  return { query: t };
}

function leIsMigrationError(e: any): boolean {
  return !!e && (e.code === "migration" || (e.status === 503 && /migrat/i.test(String(e.message || ""))));
}

/* the two lead-list signals (server: score.leadSignals) on a Find result or a
   pipeline row — the website pitch and the Google-profile pitch */
const LE_SIGNALS: Record<string, string> = { no_website: "No website", few_reviews: "Few reviews" };

/* Find results in pitch order once a lead filter is on: no website first —
   and among those, proven demand first (a 4.8★ with 40 reviews and nowhere to
   send people is the best lead there is) — then the rest by fewest reviews.
   With no filter on, Google's own order stays. */
function leSortFound(list: any[], opts?: { noSite?: boolean; lowRev?: boolean }): any[] {
  const out = (list || []).slice();
  if (!opts || (!opts.noSite && !opts.lowRev)) return out;
  const rev = (r: any) => (r && r.reviews != null && Number.isFinite(Number(r.reviews)) ? Number(r.reviews) : 0);
  const rate = (r: any) => (r && r.rating != null && Number.isFinite(Number(r.rating)) ? Number(r.rating) : 0);
  return out.sort((a, b) => {
    const an = a.website ? 1 : 0, bn = b.website ? 1 : 0;
    if (an !== bn) return an - bn;
    if (!a.website) return rate(b) - rate(a) || rev(b) - rev(a);
    return rev(a) - rev(b) || rate(b) - rate(a);
  });
}

/* two pages of the same search ("Load more") → one summary */
function leMergeSummary(a: any, b: any): any {
  if (!a) return b || null;
  if (!b) return a;
  const n = (v: any) => Number(v) || 0;
  return { ...a, ...b, scanned: n(a.scanned) + n(b.scanned), shown: n(a.shown) + n(b.shown), noWebsite: n(a.noWebsite) + n(b.noWebsite), fewReviews: n(a.fewReviews) + n(b.fewReviews),
    inPipeline: n(a.inPipeline) + n(b.inPipeline), pages: n(a.pages) + n(b.pages), queries: Math.max(n(a.queries), n(b.queries)), stopped: b.stopped || a.stopped || undefined };
}

/* "Scanned 58 businesses (3 pages · 3 phrasings) · 14 with no website · 9 under 10 reviews · showing 14 · 3 already in your pipeline" */
function leFindSummary(s: any, inPipeline: number): string {
  if (!s) return "";
  const n = (v: any) => (Number(v) || 0).toLocaleString("en-US");
  const how = [Number(s.pages) > 1 ? n(s.pages) + " pages" : "", Number(s.queries) > 1 ? n(s.queries) + " phrasings" : ""].filter(Boolean).join(" · ");
  const bits = ["Scanned " + n(s.scanned) + " business" + (Number(s.scanned) === 1 ? "" : "es") + (how ? " (" + how + ")" : ""), n(s.noWebsite) + " with no website", n(s.fewReviews) + " under " + n(s.reviewsUnder || 10) + " reviews"];
  if (Number(s.shown) !== Number(s.scanned)) bits.push("showing " + n(s.shown));
  if (inPipeline) bits.push(n(inPipeline) + " already in your pipeline");
  if (s.stopped === "budget") bits.push("stopped early — today's Google limit is used up");
  return bits.join(" · ");
}

/* a timeline event → what the owner reads */
function leEventView(ev: any): { icon: string; title: string; detail: string } {
  const m = (ev && ev.meta) || {};
  const type = String((ev && ev.type) || "");
  const money = (n: any) => "$" + Math.round(Number(n) || 0).toLocaleString("en-US");
  const kind = (k: any) => LE_KIND_LABEL[String(k || "")] || (k ? String(k) + " form" : "a form");
  const scorecard = (sc: any) => {
    if (!sc) return "";
    const bits: string[] = [];
    const top = (sc.top || []).map((t: any) => t.short || t.q || t.id).filter(Boolean);
    if (top.length) bits.push("Top problems: " + top.slice(0, 3).join(", "));
    if (sc.hours) bits.push("~" + sc.hours + " h/week");
    if (sc.monthly) bits.push(money(sc.monthly) + "/mo");
    if (sc.plan) bits.push("pointed to " + sc.plan);
    return bits.join(" · ");
  };
  const lead = () => [m.summary ? String(m.summary).slice(0, 300) : "", scorecard(m.scorecard), m.message ? "“" + String(m.message).slice(0, 240) + "”" : "", m.team_size ? "Team: " + m.team_size : ""].filter(Boolean).join(" · ");
  const text = m.text ? String(m.text) : "";
  let title = type.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
  let detail = "";
  switch (type) {
    case "created":
      if (m.source === "inbound") { title = m.kind === "scorecard" ? "Filled in the Fence Ops Scorecard" : "Came in from the " + kind(m.kind); detail = lead(); }
      else if (m.source === "stalled_signup") { title = "Signed up but never sent a quote"; detail = [m.plan ? "Plan: " + m.plan : "", m.email || "", m.signed_up_at ? "signed up " + leTimeAgo(m.signed_up_at) : ""].filter(Boolean).join(" · "); }
      else if (m.source === "places") title = "Added from a Google search";
      else if (m.source === "link") title = "Added from a pasted link";
      else { title = "Added to the pipeline"; detail = m.website ? String(m.website) : ""; }
      break;
    case "imported": title = "Imported from " + (m.file ? String(m.file) : "a CSV"); break;
    case "merged": {
      const said: Record<string, string> = { owner_name: "contact", phone_e164: "phone", place_id: "Google place", source_detail: "source detail", external_id: "list ID" };
      const f: string[] = (m.filled || []).map((k: any) => said[String(k)] || String(k).replace(/_/g, " "));
      title = f.length && f.every((k) => k === "tags" || k === "notes") ? "A later import added " + f.join(" and ") : "A later import filled in blanks";
      detail = f.join(", ") + (m.file ? " — from " + String(m.file) : "");
      break;
    }
    case "enriched": title = "Read their website"; detail = [m.pages ? m.pages + " pages" : "", m.score != null ? "score " + m.score : ""].filter(Boolean).join(" · "); break;
    case "enrich_failed": title = "Couldn't read their website"; detail = String(m.error || m.reason || ""); break;
    case "site_built": title = m.rebuilt ? "Preview site rebuilt" : "Preview site built"; break;
    case "site_extended": title = "Preview extended"; detail = m.expiresAt || m.expires_at ? "now expires " + new Date(m.expiresAt || m.expires_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""; break;
    case "site_deleted": title = "Preview site deleted"; break;
    case "preview_viewed": title = "Opened their preview site"; detail = m.views ? m.views + " views so far" : ""; break;
    case "claimed": title = "Claimed their site"; break;
    case "signed_up": title = m.stalled ? "Signed up (hasn't sent a quote yet)" : "Signed up for Fence Works"; detail = [m.plan ? "Plan: " + m.plan : "", m.status && m.status !== "free" ? m.status : ""].filter(Boolean).join(" · "); break;
    case "stage_changed":
      title = leStageLabel(m.from) + " → " + leStageLabel(m.to);
      detail = m.reason ? String(m.reason) : m.via === "signup" ? "from their signup" : m.via === "bulk" ? "with others, from the pipeline" : m.via === "outreach" ? "when the first email went" : m.via ?"after a " + String(m.via).split(":")[0] + (String(m.via).includes(":") ? " (" + String(m.via).split(":")[1].replace(/_/g, " ") + ")" : "") : "";
      break;
    case "note": title = "Note"; detail = text; break;
    case "call": { const o = LE_OUTCOMES.find((x) => x[0] === m.outcome); title = "Call" + (o ? " — " + o[1] : ""); detail = text; break; }
    case "email": title = "Emailed them"; detail = text; break;
    case "dm": title = "Sent a DM"; detail = text; break;
    case "inbound": title = (m.kind === "scorecard" ? "Filled in the Fence Ops Scorecard" : "Came in again from the " + kind(m.kind)); detail = lead(); break;
    case "do_not_contact": title = m.on ? "Marked do-not-contact" : "Do-not-contact removed"; break;
    case "replied": title = m.via === "manual" ? "Marked replied" : "Replied to the email"; detail = text; break;
    case "unsubscribed": title = "Unsubscribed"; detail = m.via === "email" ? "by email" : m.via === "one_click" ? "from their mail app" : ""; break;
    case "bounced": title = m.soft ? "Email bounced (temporary — nothing stopped)" : "Email bounced — the address is suppressed"; detail = m.detail ? String(m.detail) : m.to ? String(m.to) : ""; break;
    case "complained": title = "Marked the email as spam"; detail = "The address and their domain are suppressed."; break;
    case "outreach": {
      const act: Record<string, string> = { started: "Pitch started", paused: "Pitch paused", resumed: "Pitch resumed", stopped: "Pitch stopped", completed: "Pitch finished", step_skipped: "Pitch step skipped", send_failed: "An email didn't send" };
      const why: Record<string, string> = { connected_call: "you connected on a call", owner: "by you", replied: "they replied", unsubscribed: "they unsubscribed", bounced: "the email bounced", complained: "marked as spam",
        claimed: "they claimed their site", signed_up: "they signed up", lost: "marked lost", not_fit: "marked not a fit", do_not_contact: "marked do-not-contact", suppressed: "on the do-not-contact list",
        no_email: "no email address", preview_expired: "the preview expired", deleted: "prospect deleted", dismissed: "deleted before" };
      const step: Record<string, string> = { email1: "email 1", call1: "the day 2 call", email2: "email 2", email3: "email 3" };
      title = act[m.action] || "Outreach";
      detail = m.reason ? why[m.reason] || String(m.reason).replace(/_/g, " ") : m.error ? String(m.error) : m.step ? step[m.step] || String(m.step) : "";
      break;
    }
    case "email_sent": title = "Email sent" + (m.name ? " — " + m.name : ""); detail = m.subject ? "“" + m.subject + "”" : ""; break;
    case "email_opened": title = "Opened the email"; detail = "Opens are approximate — some mail apps open every email."; break;
    case "clicked": title = m.link === "scorecard" ? "Clicked through to the scorecard" : "Clicked the link to their new site"; break;
    case "call_task": title = "Call task on Today"; detail = m.name ? String(m.name) : ""; break;
    default: detail = text || (m.summary ? String(m.summary).slice(0, 300) : "");
  }
  return { icon: LE_EVENT_ICON[type] || "bell", title, detail };
}

/* only http(s) links and https images from anything scraped */
function leSafeHref(u: any): string | null {
  const s = String(u == null ? "" : u).trim();
  return /^https?:\/\/[^\s"'<>]+$/i.test(s) ? s : null;
}
function leSafeImg(u: any): string | null {
  const s = String(u == null ? "" : u).trim();
  return /^https:\/\/[^\s"'<>]+$/i.test(s) ? s : null;
}

/* ---------------- plumbing ---------------- */
const leApi = (): any => (window.VFP_DB && (window.VFP_DB as any).leadengine) || null;
const toDateInput = (v: any) => { const t = v ? Date.parse(v) : NaN; if (!Number.isFinite(t)) return ""; const d = new Date(t); return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0"); };
const shortDate = (v: any) => { const t = v ? Date.parse(v) : NaN; return Number.isFinite(t) ? new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—"; };
const dateTime = (v: any) => { const t = v ? Date.parse(v) : NaN; return Number.isFinite(t) ? new Date(t).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"; };
const place = (p: any) => [p && p.city, p && p.state].filter(Boolean).join(", ");
const telOf = (p: any) => { const d = String((p && (p.phone_e164 || p.phone_display)) || "").replace(/[^\d+]/g, ""); return d.replace(/\D/g, "").length >= 10 ? "tel:" + d : null; };
function leErrText(e: any): string {
  if (!e) return "";
  if (e.status === 404 && /^leadengine 404$/.test(String(e.message))) return "This part of the Lead Engine isn't on the server yet.";
  return String(e.message || e);
}

function useLeLoad(fn: (api: any) => Promise<any>, deps: any[] = []) {
  const api = leApi();
  const live = !!(api && api.enabled && api.enabled());
  const [data, setData] = React.useState<any>(null);
  const [denied, setDenied] = React.useState<any>(false);
  const [migration, setMigration] = React.useState<any>(false);
  const [err, setErr] = React.useState<any>("");
  const seq = React.useRef(0);
  const load = React.useCallback(() => {
    if (!live) return Promise.resolve();
    const my = ++seq.current;
    return fn(api).then((d: any) => { if (my === seq.current) { setData(d); setErr(""); setMigration(false); } })
      .catch((e: any) => {
        if (my !== seq.current) return;
        if ((e && e.status === 403) || /owner only|forbidden/i.test(String((e && e.message) || e))) setDenied(true);
        else if (leIsMigrationError(e)) setMigration(true);
        else setErr(leErrText(e));
      });
  }, [live, ...deps]);
  React.useEffect(() => { load(); }, [load]);
  return { api, live, data, setData, denied, migration, err, load };
}
/* refresh while the tab is visible */
function usePoll(fn: () => any, ms: number, on = true) {
  React.useEffect(() => {
    if (!on) return;
    const t = setInterval(() => { if (typeof document === "undefined" || document.visibilityState !== "hidden") fn(); }, ms);
    return () => clearInterval(t);
  }, [fn, ms, on]);
}

function LeGuard({ live, denied, migration }: any) {
  if (!live) return <div className="page fade-up" style={{ maxWidth: 640 }}><div className="card pad" style={{ textAlign: "center" }}><EmptyState icon="target" title="Needs the live service" sub="The Lead Engine runs on the automation service (app.fenceworks.ai)." /></div></div>;
  if (denied) return <div className="page fade-up" style={{ maxWidth: 640 }}><div className="card pad" style={{ textAlign: "center" }}><EmptyState icon="lock" title="Owner only" sub="Limited to the platform owner's account (SUPPORT_OWNER_EMAILS, verified)." /></div></div>;
  if (migration) return <LeMigrationCard />;
  return null;
}
function LeMigrationCard() {
  return (
    <div className="page fade-up" style={{ maxWidth: 680 }}>
      <div className="card pad le-setup">
        <div className="kicker" style={{ color: "var(--amber)" }}>One step before the Lead Engine works</div>
        <h2 className="display" style={{ fontSize: 22, margin: "6px 0 8px" }}>Run migration #25 in Supabase</h2>
        <p className="muted" style={{ margin: "0 0 10px", fontSize: 14 }}>The Lead Engine's tables (prospects, their timeline, preview sites, the do-not-contact list) aren't in the database yet.</p>
        <ol style={{ margin: "0 0 12px", paddingLeft: 20, fontSize: 14, lineHeight: 1.7 }}>
          <li>Open Supabase → <b>SQL editor</b>.</li>
          <li>Paste <code>supabase/migrate_2026-10-03_lead_engine.sql</code> and press <b>Run</b>. It's safe to run twice.</li>
          <li>Come back and refresh this page.</li>
        </ol>
        <button className="btn btn-primary btn-sm" onClick={() => window.location.reload()}><Icon name="undo" size={13} />Refresh</button>
      </div>
    </div>
  );
}

function LeTabs({ route, go, due }: any) {
  const tabs: [string, string][] = [["leadengine", "Pipeline"], ["leadengine-today", "Today"], ["leadengine-outreach", "Outreach"], ["leadengine-find", "Find prospects"]];
  return (
    <div className="le-tabs" role="tablist" aria-label="Lead Engine">
      {tabs.map(([r, lb]) => (
        <button key={r} role="tab" aria-selected={route === r} className={"chip" + (route === r ? " teal" : "")} style={{ cursor: "pointer" }} onClick={() => go(r)}>
          {lb}{r === "leadengine-today" && due ? <span className="le-count">{due}</span> : null}
        </button>
      ))}
    </div>
  );
}

function LeScore({ score, breakdown, big }: any) {
  const b = leScoreBand(score);
  const lines = leBreakdownLines(breakdown);
  const title = (score == null ? "Not scored yet — the score comes from reading their website" : "Opportunity score " + score + "/100 · " + b.label) + (lines.length ? "\n" + lines.join("\n") : "");
  return <span className={"le-score" + (big ? " big" : "")} style={{ color: b.color }} title={title} aria-label={title}>{score == null ? "—" : score}</span>;
}
function LeStage({ stage }: any) {
  const s = LE_STAGES.find((x) => x[0] === stage) || [stage, leStageLabel(stage), "var(--fg-3)"];
  return <span className="chip" style={{ fontSize: 11, padding: "1px 8px", color: s[2], borderColor: "currentColor", background: "transparent", whiteSpace: "nowrap" }}>{s[1]}</span>;
}
function LeStat({ label, value, sub, accent, live }: any) {
  return (
    <div className="card" style={{ padding: "14px 16px" }}>
      <div className="muted" style={{ fontSize: 12 }}>{label}</div>
      <div className="display tnum" style={{ fontSize: 24, marginTop: 2, color: accent || undefined, display: "flex", alignItems: "center", gap: 8 }}>{live && <span className="le-live-dot" aria-hidden />}{value}</div>
      {sub && <div className="faint" style={{ fontSize: 11.5, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
function LeSection({ title, right, children, className = "", pad = true }: any) {
  return (
    <div className={"card " + className} style={{ padding: pad ? 16 : 0, minWidth: 0 }}>
      {(title || right) && <div className="row between" style={{ marginBottom: 12, gap: 8, flexWrap: "wrap", padding: pad ? 0 : "14px 16px 0" }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>{title}</h3>{right}
      </div>}
      {children}
    </div>
  );
}
function LeModal({ title, sub, onClose, children, wide }: any) {
  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className={"modal" + (wide ? " modal-lg" : "")} onClick={(e: any) => e.stopPropagation()} style={{ padding: 20 }}>
        <div className="row between" style={{ alignItems: "flex-start", gap: 10, marginBottom: 12 }}>
          <div style={{ minWidth: 0 }}>
            <h3 className="display" style={{ margin: 0, fontSize: 19 }}>{title}</h3>
            {sub && <div className="muted" style={{ fontSize: 13, marginTop: 3 }}>{sub}</div>}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close"><Icon name="x" size={14} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
const lab: any = { fontSize: 12, fontWeight: 600, color: "var(--fg-3)", margin: "10px 0 4px", display: "block" };
/* a US state picker; `keep` shows a value already on file that isn't a state
   (from before states were checked) so opening a form never silently drops it */
function LeStateSelect({ value, onChange, id, keep, blank = "—", ...rest }: any) {
  const v = String(value || "");
  const odd = v && !LE_US_STATES.some((s) => s[0] === v) ? v : "";
  return (
    <select id={id} className="select" value={v} onChange={(e: any) => onChange(e.target.value)} {...rest}>
      <option value="">{blank}</option>
      {odd && keep && <option value={odd}>{odd} (not a US state)</option>}
      {LE_US_STATES.map(([ab, name]) => <option key={ab} value={ab}>{ab} — {name}</option>)}
    </select>
  );
}

/* ============================================================
   Add a prospect · paste a link · import a CSV
   ============================================================ */
function AddProspectModal({ onClose, go }: any) {
  const api = leApi();
  const [f, setF] = React.useState<any>({ business_name: "", website: "", phone: "", email: "", contact_first_name: "", contact_last_name: "", city: "", state: "", notes: "" });
  const [busy, setBusy] = React.useState<any>(false);
  const [err, setErr] = React.useState<any>("");
  const set = (k: string, v: any) => setF((x: any) => ({ ...x, [k]: v }));
  async function save(e?: any) {
    if (e) e.preventDefault();
    if (!f.business_name.trim()) { setErr("Give it the business name."); return; }
    setBusy(true); setErr("");
    try {
      // adding one by hand is deliberate: it comes in even if you deleted it before
      const r = await api.create({ ...f, source: "manual" });
      if (r.duplicateOf) notify((r.prospect && r.prospect.business_name ? r.prospect.business_name : "That business") + " is already in your pipeline — here it is.");
      else notify("Added. " + (r.prospect.enrich_status === "queued" ? "Reading their website now." : "No website to read — add one if you find it."));
      onClose();
      go("prospect", { id: r.duplicateOf || r.prospect.id });
    } catch (x: any) { setErr(leErrText(x)); } finally { setBusy(false); }
  }
  const input = (k: string, label: string, props: any = {}) => (
    <div style={{ minWidth: 0 }}><label style={lab} htmlFor={"le-add-" + k}>{label}</label><input id={"le-add-" + k} className="input" value={f[k]} onChange={(e) => set(k, e.target.value)} {...props} /></div>
  );
  return (
    <LeModal title="Add a prospect" sub="Their website is the important part — we read it, audit it and score them." onClose={onClose}>
      <form onSubmit={save}>
        {input("business_name", "Business name *", { autoFocus: true, placeholder: "Smith Fence Co" })}
        <div className="le-2col">
          {input("website", "Website", { placeholder: "smithfence.com", inputMode: "url" })}
          {input("phone", "Phone", { placeholder: "(920) 555-0142", inputMode: "tel" })}
        </div>
        <div className="le-2col">
          {input("contact_first_name", "Contact first name", { placeholder: "Jim", autoComplete: "off" })}
          {input("contact_last_name", "Last name", { placeholder: "Smith", autoComplete: "off" })}
        </div>
        <div className="le-2col">
          {input("email", "Email", { placeholder: "owner@smithfence.com", inputMode: "email" })}
          <div className="le-2col" style={{ gridTemplateColumns: "1fr 150px" }}>
            {input("city", "City", { placeholder: "Green Bay" })}
            <div style={{ minWidth: 0 }}><label style={lab} htmlFor="le-add-state">State</label><LeStateSelect id="le-add-state" value={f.state} onChange={(v: string) => set("state", v)} /></div>
          </div>
        </div>
        <label style={lab} htmlFor="le-add-notes">Notes</label>
        <textarea id="le-add-notes" className="textarea" rows={3} value={f.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Where you found them, who the owner is…" style={{ width: "100%" }} />
        {err && <p style={{ color: "var(--red)", fontSize: 13, margin: "8px 0 0" }}>{err}</p>}
        <div className="row gap-6" style={{ justifyContent: "flex-end", marginTop: 14 }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>{busy ? "Adding…" : "Add prospect"}</button>
        </div>
      </form>
    </LeModal>
  );
}

function PasteLinkModal({ onClose, go }: any) {
  const api = leApi();
  const [url, setUrl] = React.useState<any>("");
  const [busy, setBusy] = React.useState<any>(false);
  const [err, setErr] = React.useState<any>("");
  async function save(e?: any) {
    if (e) e.preventDefault();
    if (!url.trim()) return;
    setBusy(true); setErr("");
    try {
      const r = await api.addLink(url.trim());
      notify(r.duplicateOf ? "Already in your pipeline — here it is." : "Added. Reading their website now.");
      onClose();
      go("prospect", { id: r.duplicateOf || r.prospect.id });
    } catch (x: any) { setErr(leErrText(x)); } finally { setBusy(false); }
  }
  return (
    <LeModal title="Paste a link" sub="Their website, or their Google Maps listing. We'll find the rest." onClose={onClose}>
      <form onSubmit={save}>
        <input className="input" autoFocus inputMode="url" placeholder="https://smithfence.com  or  https://maps.app.goo.gl/…" value={url} onChange={(e) => setUrl(e.target.value)} />
        {err && <p style={{ color: "var(--red)", fontSize: 13, margin: "8px 0 0" }}>{err}</p>}
        <div className="row gap-6" style={{ justifyContent: "flex-end", marginTop: 14 }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !url.trim()}>{busy ? "Adding…" : "Add to pipeline"}</button>
        </div>
      </form>
    </LeModal>
  );
}

const IMPORT_CHUNK = 500;
const DRY_MAX = 2000;   // the server's per-call cap: the dry run checks up to this many rows in one go
const LE_NO_DEFAULTS = { tags: [] as string[], state: "", city: "", stage: "", source_detail: "", notes: "" };

/* type-and-enter chips (the import's "Apply to every row" tags) */
function LeTagChips({ tags, onChange, id, placeholder }: any) {
  const [t, setT] = React.useState<any>("");
  const list: string[] = tags || [];
  const add = () => {
    const parts = String(t).split(/[,;|]/).map((s) => s.trim().toLowerCase().replace(/\s+/g, " ").slice(0, 40)).filter(Boolean);
    setT("");
    if (parts.length) onChange([...list, ...parts.filter((x, i) => !list.includes(x) && parts.indexOf(x) === i)].slice(0, 20));
  };
  return (
    <div className="le-chipin" onClick={(e: any) => { const i = e.currentTarget.querySelector("input"); if (i && e.target === e.currentTarget) i.focus(); }}>
      {list.map((x) => <span key={x} className="chip" style={{ fontSize: 11.5 }}>{x}<button type="button" aria-label={"Remove " + x} onClick={() => onChange(list.filter((y) => y !== x))}>×</button></span>)}
      <input id={id} value={t} onChange={(e) => setT(e.target.value)} placeholder={list.length ? "" : placeholder}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } else if (e.key === "Backspace" && !t && list.length) onChange(list.slice(0, -1)); }}
        onBlur={add} aria-label="Tags for every row" />
    </div>
  );
}

function ImportCsvModal({ onClose, onDone }: any) {
  const api = leApi();
  const fileRef = React.useRef<any>(null);
  const [file, setFile] = React.useState<any>(null);
  const [parsed, setParsed] = React.useState<any>(null);
  const [mapping, setMapping] = React.useState<any>({});
  const [defaults, setDefaults] = React.useState<any>(LE_NO_DEFAULTS);
  const [includeDeleted, setIncludeDeleted] = React.useState<any>(false);
  const [dry, setDry] = React.useState<any>(null);
  const [step, setStep] = React.useState<any>("pick");
  const [progress, setProgress] = React.useState<any>({ done: 0, total: 0 });
  const [result, setResult] = React.useState<any>(null);
  const [err, setErr] = React.useState<any>("");
  const [drag, setDrag] = React.useState<any>(false);
  const setD = (k: string, v: any) => setDefaults((d: any) => ({ ...d, [k]: v }));

  async function read(f: File) {
    if (!f) return;
    setErr("");
    if (f.size > 15_000_000) { setErr("That file is over 15 MB — split it up first."); return; }
    const text = await f.text();
    const rec = csvRecords(text);
    if (!rec.headers.length || !rec.rows.length) { setErr("No rows in that file. It needs a header row and at least one line."); return; }
    // a few values per column, so a "Region: Panhandle" column isn't taken for State
    const samples: Record<string, any[]> = {};
    for (const h of rec.headers) samples[h] = rec.rows.slice(0, 50).map((r: any) => r[h]).filter((v: any) => v != null && String(v).trim() !== "");
    setFile(f); setParsed(rec); setMapping(leAutoMap(rec.headers, samples)); setDefaults(LE_NO_DEFAULTS); setIncludeDeleted(false); setDry(null); setStep("map");
  }
  const rows = React.useMemo(() => (parsed ? leRowsFromMapping(parsed.rows, mapping) : []), [parsed, mapping]);
  const named = rows.filter((r) => r.business_name).length;
  const hasName = Object.values(mapping).includes("business_name");
  // what's sent: blanks left out, so a row's own value always wins
  const sentDefaults = React.useMemo(() => {
    const o: any = {};
    if (defaults.tags.length) o.tags = defaults.tags;
    for (const k of ["state", "city", "stage", "source_detail", "notes"]) if (String(defaults[k] || "").trim()) o[k] = String(defaults[k]).trim();
    return o;
  }, [defaults]);

  // the dry run: what this file would do, before anything is written
  const dryKey = step === "map" && hasName && named ? JSON.stringify([mapping, sentDefaults, includeDeleted, rows.length]) : "";
  React.useEffect(() => {
    if (!dryKey) { setDry(null); return; }
    let live = true;
    setDry((d: any) => ({ ...(d || {}), loading: true }));
    const t = setTimeout(async () => {
      try {
        const r = await api.importRows(rows.slice(0, DRY_MAX), "csv", file && file.name, { defaults: sentDefaults, includeDeleted, dryRun: true });
        if (live) setDry({ data: r, loading: false });
      } catch (e: any) { if (live) setDry({ err: leIsMigrationError(e) ? "Run migration #25 (supabase/migrate_2026-10-03_lead_engine.sql) first." : leErrText(e), loading: false }); }
    }, 400);
    return () => { live = false; clearTimeout(t); };
  }, [dryKey]);

  async function run() {
    const chunks = leChunks(rows, IMPORT_CHUNK);
    setStep("importing"); setProgress({ done: 0, total: rows.length });
    const sum: any = { created: 0, matched: 0, inFile: 0, filled: 0, skippedDeleted: 0, restored: 0, warningCount: 0, errors: [], errorCount: 0 };
    for (let i = 0; i < chunks.length; i++) {
      try {
        const r = await api.importRows(chunks[i], "csv", file && file.name, { defaults: sentDefaults, includeDeleted });
        for (const k of ["created", "matched", "inFile", "filled", "skippedDeleted", "restored", "warningCount"]) sum[k] += Number((r as any)[k]) || 0;
        if (r.matched == null) sum.matched += r.duplicates || 0;   // an older server
        sum.errorCount += r.errorCount != null ? r.errorCount : (r.errors || []).length;
        for (const e of r.errors || []) if (sum.errors.length < 50) sum.errors.push({ line: e.row == null ? null : i * IMPORT_CHUNK + e.row + 2, error: e.error });
      } catch (e: any) {
        if (leIsMigrationError(e)) { setErr("Run migration #25 (supabase/migrate_2026-10-03_lead_engine.sql) first."); setStep("map"); return; }
        sum.errorCount += chunks[i].length;
        sum.errors.push({ line: null, error: "Rows " + (i * IMPORT_CHUNK + 2) + "–" + (i * IMPORT_CHUNK + chunks[i].length + 1) + " weren't saved: " + leErrText(e) });
      }
      setProgress({ done: Math.min(rows.length, (i + 1) * IMPORT_CHUNK), total: rows.length });
    }
    setResult(sum); setStep("done");
    onDone && onDone();
  }

  const sample = (h: string) => { const r = parsed && parsed.rows.find((x: any) => x[h]); return r ? String(r[h]).slice(0, 60) : ""; };
  const d = dry && dry.data;
  const badStates = d && d.warnings ? [...new Set(d.warnings.filter((w: any) => w.field === "state").map((w: any) => w.value))] : [];
  return (
    <LeModal title="Import a CSV" wide sub="License-board lists, association members, trade-show lists. Duplicates are matched by the list's own ID, Google place, website, phone, email, then name + town — never overwritten: their blanks are filled and tags and notes added." onClose={step === "importing" ? () => {} : onClose}>
      {step === "pick" && (
        <div className={"le-drop" + (drag ? " on" : "")} onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files && e.dataTransfer.files[0]; if (f) read(f); }}>
          <Icon name="upload" size={24} style={{ opacity: .6 }} />
          <div style={{ fontWeight: 700, marginTop: 8 }}>Drop a CSV here</div>
          <div className="faint" style={{ fontSize: 12.5, margin: "2px 0 12px" }}>or</div>
          <button className="btn btn-primary btn-sm" onClick={() => fileRef.current && fileRef.current.click()}>Choose a file</button>
          <input ref={fileRef} type="file" accept=".csv,.tsv,.txt,text/csv" style={{ display: "none" }} onChange={(e) => read(e.target.files && e.target.files[0])} />
          {err && <p style={{ color: "var(--red)", fontSize: 13, margin: "12px 0 0" }}>{err}</p>}
        </div>
      )}
      {step === "map" && parsed && (
        <div>
          <div className="row between" style={{ gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
            <div style={{ fontSize: 13 }}><b>{file && file.name}</b> · {parsed.rows.length.toLocaleString("en-US")} rows · {parsed.headers.length} columns</div>
            <button className="btn btn-ghost btn-sm" onClick={() => { setStep("pick"); setParsed(null); }}>Pick another file</button>
          </div>
          <div className="le-map">
            {parsed.headers.map((h: string) => (
              <div key={h} className="le-map-row">
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h}</div>
                  <div className="faint" style={{ fontSize: 11.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sample(h) || "—"}</div>
                </div>
                <select className="select" aria-label={"Map column " + h} value={mapping[h] || ""} onChange={(e) => setMapping((m: any) => ({ ...m, [h]: e.target.value }))} style={{ fontSize: 12.5, padding: "6px 30px 6px 10px" }}>
                  <option value="">— skip —</option>
                  {LE_FIELDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
            ))}
          </div>

          <fieldset className="le-apply">
            <legend>Apply to every row</legend>
            <div className="faint" style={{ fontSize: 12, margin: "-2px 0 4px" }}>Tags are added to every row. The rest only fill a row's blanks — a row's own value wins.</div>
            <div className="le-apply-grid">
              <div className="le-apply-tags"><label style={lab} htmlFor="le-imp-tags">Tags</label><LeTagChips id="le-imp-tags" tags={defaults.tags} onChange={(v: string[]) => setD("tags", v)} placeholder="Type a tag and press Enter — fl list v8.2" /></div>
              <div><label style={lab} htmlFor="le-imp-state">State</label><LeStateSelect id="le-imp-state" value={defaults.state} onChange={(v: string) => setD("state", v)} blank="As in the file" /></div>
              <div><label style={lab} htmlFor="le-imp-city">City</label><input id="le-imp-city" className="input" value={defaults.city} onChange={(e) => setD("city", e.target.value)} placeholder="As in the file" /></div>
              <div><label style={lab} htmlFor="le-imp-stage">Stage</label>
                <select id="le-imp-stage" className="select" value={defaults.stage} onChange={(e) => setD("stage", e.target.value)}>
                  <option value="">New (default)</option>
                  {LE_STAGES.filter(([s]) => s !== "new").map(([s, l]) => <option key={s} value={s}>{l}</option>)}
                </select>
              </div>
              <div><label style={lab} htmlFor="le-imp-src">Source detail</label><input id="le-imp-src" className="input" value={defaults.source_detail} onChange={(e) => setD("source_detail", e.target.value)} placeholder="FL list v8.2" maxLength={200} /></div>
              <div className="le-apply-notes"><label style={lab} htmlFor="le-imp-notes">Notes</label><textarea id="le-imp-notes" className="textarea" rows={1} value={defaults.notes} onChange={(e) => setD("notes", e.target.value)} placeholder="Where this list came from" style={{ width: "100%" }} /></div>
            </div>
          </fieldset>

          <div className="le-preview-note" aria-live="polite">
            {!hasName ? <span style={{ color: "var(--amber)" }}>Pick which column is the <b>Business name</b> — it's the one thing every row needs.</span>
              : dry && dry.err ? <span style={{ color: "var(--red)" }}>Couldn't check the file: {dry.err}</span>
              : d ? (
                <>
                  <div className="le-dry"><b>{leDrySummary(d, includeDeleted)}</b>{dry.loading && <span className="le-spin" aria-label="Checking" style={{ marginLeft: 8 }} />}</div>
                  {d.deleted > 0 && (
                    <label className="row gap-6 le-dry-back"><input type="checkbox" checked={!!includeDeleted} onChange={(e) => setIncludeDeleted(e.target.checked)} />Bring back the {d.deleted === 1 ? "one" : d.deleted.toLocaleString("en-US")} you deleted before</label>
                  )}
                  {badStates.length > 0 && <div style={{ color: "var(--amber)", fontSize: 12.5, marginTop: 4 }}>{d.warningCount} row{d.warningCount === 1 ? " has" : "s have"} a State that isn't a US state ({badStates.slice(0, 4).join(", ")}{badStates.length > 4 ? "…" : ""}) — left blank{defaults.state ? ", then " + defaults.state + " from Apply to every row" : ""}.</div>}
                  {rows.length > DRY_MAX && <div className="faint" style={{ fontSize: 12, marginTop: 4 }}>Checked the first {DRY_MAX.toLocaleString("en-US")} rows; the rest are matched the same way when you import.</div>}
                </>
              ) : <span className="muted">Checking {named.toLocaleString("en-US")} rows against your pipeline…</span>}
          </div>
          {err && <p style={{ color: "var(--red)", fontSize: 13, margin: "8px 0 0" }}>{err}</p>}
          <div className="row gap-6" style={{ justifyContent: "flex-end", marginTop: 14 }}>
            <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary btn-sm" disabled={!hasName || !named} onClick={run}><Icon name="upload" size={13} />Import {named.toLocaleString("en-US")} rows</button>
          </div>
        </div>
      )}
      {step === "importing" && (
        <div style={{ padding: "20px 4px" }}>
          <div style={{ fontSize: 14, marginBottom: 8 }}>Importing {progress.done.toLocaleString("en-US")} of {progress.total.toLocaleString("en-US")}…</div>
          <div className="le-bar"><span style={{ width: (progress.total ? (progress.done / progress.total) * 100 : 0) + "%" }} /></div>
        </div>
      )}
      {step === "done" && result && (
        <div>
          <div className="le-result">
            <div><div className="display tnum" style={{ fontSize: 26, color: "var(--green)" }}>{result.created.toLocaleString("en-US")}</div><div className="faint" style={{ fontSize: 12 }}>added{result.restored ? " (" + result.restored + " you'd deleted before)" : ""}</div></div>
            <div><div className="display tnum" style={{ fontSize: 26 }}>{(result.matched + result.inFile).toLocaleString("en-US")}</div><div className="faint" style={{ fontSize: 12 }}>already in the pipeline{result.filled ? " (" + result.filled + " had blanks filled or tags added)" : ""}{result.inFile ? " · " + result.inFile + " repeated in the file" : ""}</div></div>
            <div><div className="display tnum" style={{ fontSize: 26 }}>{result.skippedDeleted.toLocaleString("en-US")}</div><div className="faint" style={{ fontSize: 12 }}>deleted before — skipped</div></div>
            <div><div className="display tnum" style={{ fontSize: 26, color: result.errorCount ? "var(--amber)" : undefined }}>{result.errorCount.toLocaleString("en-US")}</div><div className="faint" style={{ fontSize: 12 }}>couldn't be added</div></div>
          </div>
          {result.warningCount > 0 && <p style={{ color: "var(--amber)", fontSize: 12.5, margin: "0 0 8px" }}>{result.warningCount} State value{result.warningCount === 1 ? " wasn't a US state and was" : "s weren't US states and were"} left blank.</p>}
          {result.errors.length > 0 && (
            <div className="le-errors">
              {result.errors.slice(0, 12).map((e: any, i: number) => <div key={i}>{e.line ? "Line " + e.line + ": " : ""}{e.error}</div>)}
              {result.errorCount > 12 && <div className="faint">…and {result.errorCount - 12} more.</div>}
            </div>
          )}
          <p className="muted" style={{ fontSize: 13 }}>{result.created ? "The ones with a website are queued to be read and scored — watch the scores fill in." : ""}</p>
          <div className="row" style={{ justifyContent: "flex-end" }}><button className="btn btn-primary btn-sm" onClick={onClose}>Done</button></div>
        </div>
      )}
    </LeModal>
  );
}

/* the three ways in, shared by the pipeline, its empty state and Find */
function useAddFlows(go: any, onChange?: () => any) {
  const [open, setOpen] = React.useState<any>(null);
  const modal = open === "add" ? <AddProspectModal go={go} onClose={() => setOpen(null)} />
    : open === "link" ? <PasteLinkModal go={go} onClose={() => setOpen(null)} />
    : open === "csv" ? <ImportCsvModal onClose={() => setOpen(null)} onDone={onChange} />
    : null;
  return { open: setOpen, modal };
}

/* ============================================================
   Pipeline
   ============================================================ */
const LE_SORTS: [string, string][] = [["score", "Best score"], ["recent", "Recently active"], ["next", "Next step"]];

/* the `leadengine` route: the pipeline, or — for the hot alert's link,
   /leadengine?prospect=<id> (pitchsite.js prospectAdminUrl) — that prospect.
   No hooks here, so switching between the two never breaks hook order. */
function LeadEngineHome({ params, go }: any) {
  const pid = params && params.prospect ? String(params.prospect) : "";
  return pid ? <ProspectPage key={pid} params={{ id: pid }} go={go} /> : <LeadEnginePage go={go} />;
}

const LE_BULK_MAX = 2000;   // the server's cap per bulk call

/* the bar that shows once anything is ticked:
   "N selected · Change stage ▾ · Add tag · Delete · Clear" */
function LeBulkBar({ count, busy, onStage, onTag, onDelete, onClear, onPitch }: any) {
  const [tagging, setTagging] = React.useState<any>(false);
  const [tag, setTag] = React.useState<any>("");
  const addTag = () => { const t = tag.trim(); if (!t) return; setTag(""); setTagging(false); onTag(t); };
  return (
    <div className="le-bulk" role="region" aria-label="Bulk actions">
      <b className="tnum">{count.toLocaleString("en-US")} selected</b>
      {onPitch && <button className="btn btn-primary btn-sm" disabled={!!busy} onClick={onPitch} title="The Website pitch: the first email waits for your approval in Outreach"><Icon name="send" size={12} />Start pitch</button>}
      <select className="select" aria-label="Change stage" value="" disabled={!!busy} onChange={(e) => { if (e.target.value) onStage(e.target.value); }}>
        <option value="">Change stage ▾</option>
        {LE_STAGES.map(([s, l]) => <option key={s} value={s}>{l}</option>)}
      </select>
      {tagging ? (
        <form className="le-bulk-tag" onSubmit={(e) => { e.preventDefault(); addTag(); }}>
          <input className="input" autoFocus value={tag} onChange={(e) => setTag(e.target.value)} placeholder="tag" aria-label="Tag to add" maxLength={40}
            onKeyDown={(e) => { if (e.key === "Escape") { setTagging(false); setTag(""); } }} />
          <button type="submit" className="btn btn-primary btn-sm" disabled={!!busy || !tag.trim()}>Add</button>
        </form>
      ) : <button className="btn btn-outline btn-sm" disabled={!!busy} onClick={() => setTagging(true)}><Icon name="plus" size={12} />Add tag</button>}
      <button className="btn btn-outline btn-sm le-bulk-del" disabled={!!busy} onClick={onDelete}><Icon name="trash" size={12} />Delete</button>
      <button className="btn btn-ghost btn-sm" disabled={!!busy} onClick={onClear}>Clear</button>
      {busy && <span className="le-spin" aria-label="Working" />}
    </div>
  );
}

function LeadEnginePage({ go }: any) {
  const [stage, setStage] = useLocalPref("le_stage", "");
  const [sort, setSort] = useLocalPref("le_sort", "score");
  const [source, setSource] = useLocalPref("le_source", "");
  const [st, setSt] = useLocalPref("le_state", "");
  const [signal, setSignal] = useLocalPref("le_signal", "");   // no_website | few_reviews — the two lead lists
  const [q, setQ] = React.useState<any>("");
  const [qq, setQq] = React.useState<any>("");
  const [sel, setSel] = React.useState<any>(() => new Set());
  const [allMatching, setAllMatching] = React.useState<any>(false);
  const [bulkBusy, setBulkBusy] = React.useState<any>(false);
  React.useEffect(() => { const t = setTimeout(() => setQq(q), 250); return () => clearTimeout(t); }, [q]);
  // a selection belongs to the filters it was made under
  React.useEffect(() => { setSel(new Set()); setAllMatching(false); }, [stage, qq, source, st, signal]);
  const { api, live, data, denied, migration, err, load } = useLeLoad((a) => a.list({ stage, q: qq, source, state: st, signal, sort, limit: 500 }), [stage, qq, source, st, signal, sort]);
  usePoll(load, 30_000, live && !denied && !migration && !bulkBusy);
  const narrow = useNarrowScreen(640);
  const flows = useAddFlows(go, load);
  const guard = <LeGuard live={live} denied={denied} migration={migration} />;
  if (!live || denied || migration) return guard;

  const rows: any[] = (data && data.prospects) || [];
  const counts = (data && data.counts) || { byStage: {}, total: 0 };
  const stats = (data && data.stats) || {};
  const setup = (data && data.setup) || {};
  const by = counts.byStage || {};
  const allCount: number = Object.keys(by).reduce((n: number, k: string) => n + (Number(by[k]) || 0), 0);
  const filtered = !!(stage || qq || source || st || signal);
  const matching: number = Number(counts.matching) || rows.length;
  const shownIds = rows.map((p) => p.id);
  const picked = shownIds.filter((id) => sel.has(id)).length;
  const allShown = rows.length > 0 && picked === rows.length;
  const count = allMatching ? matching : sel.size;
  const toggle = (id: string) => { setAllMatching(false); setSel((s: any) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; }); };
  const toggleAll = () => { setAllMatching(false); setSel(allShown ? new Set() : new Set(shownIds)); };
  const clear = () => { setSel(new Set()); setAllMatching(false); };
  const plural = (n: number) => n.toLocaleString("en-US") + " prospect" + (n === 1 ? "" : "s");

  async function bulk(body: any, ok: (r: any) => string) {
    setBulkBusy(true);
    try {
      const target = allMatching ? { filter: { stage, q: qq, source, state: st, signal } } : { ids: [...sel] };
      const r = await api.bulk({ ...target, ...body });
      const errs = (r.errors || []).length;
      notify(ok(r) + (errs ? " " + errs + " couldn't be changed" + (r.errors[0] && r.errors[0].error ? " — " + r.errors[0].error : "") + "." : ""), { kind: errs ? "info" : "success" });
      clear();
    } catch (e: any) { notify(leErrText(e), { kind: "error" }); }
    finally { setBulkBusy(false); load(); }
  }
  const bulkStage = (s: string) => bulk({ action: "stage", stage: s }, (r) => "Moved " + plural(r.done) + " to " + leStageLabel(s) + ".");
  const bulkTag = (t: string) => bulk({ action: "tag", tag: t }, (r) => "Tagged " + plural(r.done) + " “" + t.toLowerCase() + "”.");
  async function bulkPitch() {
    const n = count;
    const ok = await confirmDialog({
      title: "Start the Website pitch for " + plural(n) + "?",
      body: "Each one gets email 1 with the site you built them, a call task on day 2, then two more emails. The first emails wait for your approval in Outreach → Queue.\nAnyone without an email, a preview site, or on the do-not-contact list is skipped.",
      confirmLabel: "Start " + n.toLocaleString("en-US"),
    });
    if (ok) bulk({ action: "pitch" }, (r) => loPitchSummary(r));
  }
  async function bulkDelete() {
    const n = count;
    const ok = await confirmDialog({
      title: "Delete " + plural(n) + "?",
      body: "Their timelines and preview sites are deleted too. This can't be undone.\nA later import or the inbound sync won't bring them back — an import can if you tick “bring back the ones you deleted”. To stop working them but keep the history, change their stage to Lost or Not a fit instead.",
      confirmLabel: "Delete " + n.toLocaleString("en-US"), danger: true,
      ...(n > 25 ? { requireText: "delete " + n } : {}),
    });
    if (ok) bulk({ action: "delete" }, (r) => "Deleted " + plural(r.done) + ".");
  }

  const addButtons = (
    <>
      <button className="btn btn-primary btn-sm" onClick={() => flows.open("add")}><Icon name="plus" size={13} />Add prospect</button>
      <button className="btn btn-outline btn-sm" onClick={() => flows.open("link")}><Icon name="arrow" size={13} />Paste a link</button>
      <button className="btn btn-outline btn-sm" onClick={() => flows.open("csv")}><Icon name="upload" size={13} />Import CSV</button>
      <button className="btn btn-outline btn-sm" onClick={() => go("leadengine-find")}><Icon name="search" size={13} />Find prospects</button>
    </>
  );
  const box = (p: any) => <input type="checkbox" className="le-check-box" checked={allMatching || sel.has(p.id)} onChange={() => toggle(p.id)} onClick={(e) => e.stopPropagation()} aria-label={"Select " + p.business_name} />;
  const sub = (p: any) => [leContactName(p), place(p), p.domain].filter(Boolean).join(" · ");
  // the lead signals on the row: no website (the website pitch), few Google reviews (the profile pitch)
  const badges = (p: any) => {
    const s: string[] = Array.isArray(p.signals) ? p.signals : [];
    if (!s.length) return null;
    return (
      <span className="le-badges">
        {s.includes("no_website") && <span className="le-nosite-badge" title="No website — the website pitch">No website</span>}
        {s.includes("few_reviews") && <span className="le-few-badge" title="Few Google reviews — the Google profile pitch">{p.reviews != null ? p.reviews + " review" + (p.reviews === 1 ? "" : "s") : "few reviews"}</span>}
      </span>
    );
  };

  return (
    <div className="page fade-up le-page" style={{ maxWidth: 1400 }}>
      <div className="row between le-head" style={{ marginBottom: 10, flexWrap: "wrap", gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <h1 className="display" style={{ fontSize: 28, margin: 0 }}>Lead Engine</h1>
          <p className="muted" style={{ margin: "4px 0 0" }}>Every fence contractor you could close — found, audited, pitched with a site built for them, and worked until they sign.</p>
        </div>
        <div className="row gap-6 le-actions" style={{ flexWrap: "wrap" }}>{addButtons}</div>
      </div>
      <LeTabs route="leadengine" go={go} due={counts.dueToday} />
      {err && <p style={{ color: "var(--red)", fontSize: 13 }}>{err}</p>}
      {setup.contacts === false && (
        <div className="le-note" role="note">
          <Icon name="gear" size={14} />
          <div><b>Run migration #{(setup.migration && setup.migration.number) || 26}</b> — <code>{(setup.migration && setup.migration.file) || "supabase/migrate_2026-10-04_lead_engine_contacts.sql"}</code> in the Supabase SQL editor. {(setup.migration && setup.migration.message) || ""} Everything else works now; until then a contact's name is kept whole.</div>
        </div>
      )}

      {data && counts.total === 0 && !filtered ? (
        <div className="card pad le-empty">
          <Icon name="target" size={28} style={{ color: "var(--teal)" }} />
          <h2 className="display" style={{ fontSize: 22, margin: "10px 0 6px" }}>Your pipeline is empty</h2>
          <p className="muted" style={{ margin: "0 auto", maxWidth: 560, fontSize: 14.5 }}>The Lead Engine finds fence contractors with good reviews and bad websites, builds each one a new site, and tells you the moment they look at it.<br />Start with a Google search, a list you already have, or one business you know.</p>
          <div className="row gap-6" style={{ justifyContent: "center", flexWrap: "wrap", marginTop: 18 }}>{addButtons}</div>
          <p className="faint" style={{ fontSize: 12, margin: "14px 0 0" }}>Scorecard, demo and contact-form leads and signups that stalled show up here on their own within 5 minutes.</p>
        </div>
      ) : (
        <>
          <div className="stat-row" style={{ marginBottom: 14 }}>
            <LeStat label="New this week" value={stats.newThisWeek ?? "—"} sub={(counts.total || 0).toLocaleString("en-US") + " in the pipeline"} />
            <LeStat label="Sites built" value={stats.sitesBuilt ?? "—"} sub="preview sites live" />
            <LeStat label="Contacted" value={stats.contacted ?? "—"} sub="reached so far" />
            <LeStat label="Looking now" value={stats.lookingNow ?? 0} sub={stats.lookingNow ? "on their site in the last 10 min" : "nobody right now"} accent={stats.lookingNow ? "var(--green)" : undefined} live={!!stats.lookingNow} />
            <LeStat label="Trials" value={stats.trials ?? "—"} sub="from the pipeline" accent="var(--violet)" />
            <LeStat label="Customers this month" value={stats.customersThisMonth ?? "—"} accent="var(--green)" />
          </div>

          <div className="row gap-6" style={{ flexWrap: "wrap", marginBottom: 10, alignItems: "center" }}>
            <button className={"chip" + (!stage ? " teal" : "")} style={{ cursor: "pointer" }} onClick={() => setStage("")}>All · {allCount}</button>
            {LE_STAGES.filter(([s]) => by[s] || stage === s).map(([s, l]) => (
              <button key={s} className={"chip" + (stage === s ? " teal" : "")} style={{ cursor: "pointer" }} onClick={() => setStage(stage === s ? "" : s)}>{l} · {by[s] || 0}</button>
            ))}
            <span className="le-chip-sep" aria-hidden />
            {Object.keys(LE_SIGNALS).map((k) => (
              <button key={k} className={"chip" + (signal === k ? (k === "no_website" ? " amber" : " violet") : "")} style={{ cursor: "pointer" }} aria-pressed={signal === k} onClick={() => setSignal(signal === k ? "" : k)}
                title={k === "no_website" ? "Prospects with no website (or a parked / dead one) — the website pitch" : "Prospects with under 10 Google reviews — the Google profile pitch"}>{LE_SIGNALS[k]} · {(counts.signals || {})[k] || 0}</button>
            ))}
          </div>
          <div className="le-toolbar">
            <input className="input" type="search" placeholder="Search name, town, email, tag, phone, list ID…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search prospects" />
            <select className="select" value={source} onChange={(e) => setSource(e.target.value)} aria-label="Source">
              <option value="">Every source</option>
              {Object.keys(LE_SOURCES).map((k) => <option key={k} value={k}>{LE_SOURCES[k]}</option>)}
            </select>
            <select className="select" value={st} onChange={(e) => setSt(e.target.value)} aria-label="State">
              <option value="">Every state</option>
              {((data && data.states) || []).concat(st && !((data && data.states) || []).includes(st) ? [st] : []).map((s: string) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select className="select" value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort">
              {LE_SORTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>

          {count > 0 && <LeBulkBar count={count} busy={bulkBusy} onStage={bulkStage} onTag={bulkTag} onDelete={bulkDelete} onClear={clear} onPitch={bulkPitch} />}
          {(allShown || allMatching) && matching > rows.length && (
            <div className="le-selall">
              {allMatching
                ? <>All <b>{matching.toLocaleString("en-US")}</b> matching prospects are selected. <button className="le-link-btn" onClick={clear}>Clear selection</button></>
                : matching <= LE_BULK_MAX
                  ? <>All {rows.length.toLocaleString("en-US")} shown are selected. <button className="le-link-btn" onClick={() => setAllMatching(true)}>Select all {matching.toLocaleString("en-US")} matching</button></>
                  : <>All {rows.length.toLocaleString("en-US")} shown are selected. {matching.toLocaleString("en-US")} match — narrow the filters to act on all of them ({LE_BULK_MAX.toLocaleString("en-US")} at a time).</>}
            </div>
          )}

          {!data ? <p className="muted" style={{ textAlign: "center", padding: 24 }}>Loading…</p>
            : rows.length === 0 ? <div className="card pad"><EmptyState icon="target" title="Nothing matches" sub={signal && !(stage || qq || source || st) ? (signal === "no_website" ? "No prospect in the pipeline is without a website yet. Find prospects → No website pulls them in from Google." : "No prospect in the pipeline has under 10 Google reviews yet. Find prospects → Few reviews pulls them in from Google.") : "Clear a filter or the search."} secondary={{ label: "Clear filters", onClick: () => { setStage(""); setSource(""); setSt(""); setSignal(""); setQ(""); } }} /></div>
            : narrow ? (
              <div className="le-cards">
                <label className="row gap-6 le-cards-all"><input type="checkbox" checked={allMatching || allShown} onChange={toggleAll} aria-label={"Select all " + rows.length + " shown"} />Select all {rows.length.toLocaleString("en-US")}{matching > rows.length ? " shown" : ""}</label>
                {rows.map((p) => (
                  <ListRow key={p.id} className={(p.looking_now ? "le-hot-row" : "") + (allMatching || sel.has(p.id) ? " le-picked" : "")} onClick={() => go("prospect", { id: p.id })} ariaLabel={"Open " + p.business_name}
                    leading={<span className="le-card-lead">{box(p)}<LeScore score={p.score} /></span>}
                    title={<span>{p.looking_now && <span title="Looking at their site right now" aria-label="Looking now">👀 </span>}{p.business_name}</span>}
                    sub={<span className="le-card-sub"><LeStage stage={p.stage} /><span>{[leContactName(p), place(p), p.last_touch_at ? "touched " + leTimeAgo(p.last_touch_at) : "not contacted"].filter(Boolean).join(" · ")}</span>{badges(p)}</span>}
                    trailing={p.next_step || p.next_step_at ? <span style={{ fontSize: 12, color: leOverdue(p.next_step_at) ? "var(--red)" : "var(--fg-3)", textAlign: "right" }}>{p.next_step || "Next step"}<br />{p.next_step_at ? shortDate(p.next_step_at) : ""}</span> : null} />
                ))}
              </div>
            ) : (
              <div className="card" style={{ padding: 0 }}>
                <div style={{ overflowX: "auto" }}>
                  <table className="le-table">
                    <thead><tr>
                      <th className="le-col-check"><input type="checkbox" className="le-check-box" checked={allMatching || allShown} ref={(el: any) => { if (el) el.indeterminate = !allMatching && picked > 0 && !allShown; }} onChange={toggleAll} aria-label={"Select all " + rows.length + " shown"} /></th>
                      <th>Business</th><th>Score</th><th>Stage</th><th>Last touch</th><th>Next step</th><th>Preview site</th><th>Source</th>
                    </tr></thead>
                    <tbody>
                      {rows.map((p) => (
                        <tr key={p.id} className={"le-click" + (p.looking_now ? " le-hot" : "") + (allMatching || sel.has(p.id) ? " le-picked" : "")} tabIndex={0} onClick={() => go("prospect", { id: p.id })} onKeyDown={(e) => { if (e.key === "Enter" && e.target === e.currentTarget) go("prospect", { id: p.id }); }}>
                          <td className="le-col-check" onClick={(e) => { e.stopPropagation(); toggle(p.id); }}>{box(p)}</td>
                          <td style={{ minWidth: 220 }}>
                            <div style={{ fontWeight: 650 }}>{p.looking_now && <span className="le-eyes" title="Looking at their site right now" aria-label="Looking now">👀</span>}{p.business_name}{p.do_not_contact && <span className="chip" style={{ fontSize: 10, padding: "0 6px", marginLeft: 6, color: "var(--red)" }}>do not contact</span>}</div>
                            <div className="faint" style={{ fontSize: 11.5 }}>{sub(p)}{p.enrich_status === "queued" || p.enrich_status === "running" ? " · reading site…" : ""}</div>
                            {badges(p)}
                            {(p.tags || []).length > 0 && <div className="row gap-4" style={{ marginTop: 3, flexWrap: "wrap" }}>{p.tags.slice(0, 5).map((t: string) => <span key={t} className="chip" style={{ fontSize: 10, padding: "0 6px" }}>{t}</span>)}</div>}
                          </td>
                          <td><LeScore score={p.score} /></td>
                          <td><LeStage stage={p.stage} /></td>
                          <td style={{ whiteSpace: "nowrap", fontSize: 12.5 }}>{p.last_touch_at ? leTimeAgo(p.last_touch_at) : <span className="faint">—</span>}</td>
                          <td style={{ fontSize: 12.5 }}>{p.next_step || p.next_step_at ? <><div>{p.next_step || "Next step"}</div><div style={{ fontSize: 11.5, color: leOverdue(p.next_step_at) ? "var(--red)" : "var(--fg-3)", fontWeight: leOverdue(p.next_step_at) ? 700 : 400 }}>{p.next_step_at ? (leOverdue(p.next_step_at) ? "overdue · " : "") + shortDate(p.next_step_at) : ""}</div></> : <span className="faint">—</span>}</td>
                          <td style={{ fontSize: 12.5, whiteSpace: "nowrap" }}>{p.site ? <>{p.site.views || 0} view{p.site.views === 1 ? "" : "s"}<div className="faint" style={{ fontSize: 11 }}>{p.site.last_viewed_at ? "last " + leTimeAgo(p.site.last_viewed_at) : "not opened yet"}</div></> : <span className="faint">—</span>}</td>
                          <td style={{ fontSize: 12 }} className="muted">{LE_SOURCES[p.source] || p.source}{p.source_detail && (p.source === "csv" || p.source === "inbound") ? <div className="faint" style={{ fontSize: 11 }}>{leSourceDetail(p)}</div> : null}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          {data && counts.matching > rows.length && <p className="faint" style={{ fontSize: 12, textAlign: "center" }}>Showing {rows.length} of {counts.matching} — search or filter to narrow it down.</p>}
          {count > 0 && narrow && <div className="le-bulk-space" aria-hidden />}
        </>
      )}
      {flows.modal}
    </div>
  );
}

/* ============================================================
   Logging a touch
   ============================================================ */
function OutcomeButtons({ p, onLogged, compact, text }: any) {
  const api = leApi();
  const [busy, setBusy] = React.useState<any>(null);
  const [cb, setCb] = React.useState<any>(null);
  async function log(outcome: string, nextAt?: string) {
    setBusy(outcome);
    try {
      const r = await api.log(p.id, { type: "call", outcome, text: text || undefined, next_step_at: nextAt || undefined });
      notify("Logged: " + (LE_OUTCOMES.find((o) => o[0] === outcome) || [0, outcome])[1] + (r.prospect && r.prospect.stage !== p.stage ? " — now " + leStageLabel(r.prospect.stage) : "") + ".", { kind: "success" });
      setCb(null);
      onLogged && onLogged(r.prospect, r.event);
      if (outcome === "not_interested" && !["lost", "not_fit", "customer"].includes(r.prospect && r.prospect.stage)) {
        const ok = await confirmDialog({ title: "Mark " + p.business_name + " as lost?", body: "They said they're not interested. You can reopen them any time.", confirmLabel: "Mark lost", cancelLabel: "Keep open" });
        if (ok) { const u = await api.update(p.id, { stage: "lost", lost_reason: "Not interested (call)" }); onLogged && onLogged(u.prospect); }
      }
    } catch (e: any) { notify(leErrText(e), { kind: "error" }); } finally { setBusy(null); }
  }
  const tomorrow = () => { const d = new Date(Date.now() + 86400000); return toDateInput(d.toISOString()); };
  return (
    <div>
      <div className="le-outcomes">
        {LE_OUTCOMES.map(([k, l]) => (
          <button key={k} className={"btn btn-sm " + (k === "connected" ? "btn-primary" : k === "not_interested" ? "btn-ghost" : "btn-outline")} disabled={!!busy}
            onClick={() => (k === "call_back" ? setCb(cb ? null : tomorrow()) : log(k))} style={compact ? { padding: "6px 10px", fontSize: 12.5 } : undefined}>
            {busy === k ? "…" : l}
          </button>
        ))}
      </div>
      {cb != null && (
        <div className="row gap-6" style={{ marginTop: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span className="muted" style={{ fontSize: 12.5 }}>Call back on</span>
          <input type="date" className="input" value={cb} onChange={(e) => setCb(e.target.value)} style={{ width: 170, padding: "6px 10px" }} aria-label="Call back date" />
          <button className="btn btn-primary btn-sm" disabled={!!busy} onClick={() => log("call_back", cb)}>Save</button>
        </div>
      )}
    </div>
  );
}

function CallLogModal({ p, onClose, onLogged }: any) {
  const [text, setText] = React.useState<any>("");
  return (
    <LeModal title={"How did the call with " + p.business_name + " go?"} sub="One tap logs it on the timeline and moves the stage." onClose={onClose}>
      <OutcomeButtons p={p} text={text} onLogged={(np: any, ev: any) => { onLogged && onLogged(np, ev); if (ev) onClose(); }} />
      <label style={lab} htmlFor="le-call-note">Note (optional)</label>
      <textarea id="le-call-note" className="textarea" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Who you spoke to, what they said…" style={{ width: "100%" }} />
      <div className="row" style={{ justifyContent: "flex-end", marginTop: 10 }}><button className="btn btn-ghost btn-sm" onClick={onClose}>Didn't call — skip</button></div>
    </LeModal>
  );
}

function LostReasonModal({ p, stage, onClose, onSave }: any) {
  const [reason, setReason] = React.useState<any>("");
  const quick = stage === "not_fit" ? ["Not a fence contractor", "Too small", "Out of our area", "Out of business"] : ["Happy with their website", "Went with someone else", "Not interested", "Too expensive", "No answer after 5 tries"];
  return (
    <LeModal title={(stage === "not_fit" ? "Not a fit" : "Lost") + " — why?"} sub={"So the reports can tell you what's losing " + p.business_name + "-type deals."} onClose={onClose}>
      <div className="row gap-6" style={{ flexWrap: "wrap", marginBottom: 8 }}>
        {quick.map((r) => <button key={r} className={"chip" + (reason === r ? " teal" : "")} style={{ cursor: "pointer" }} onClick={() => setReason(r)}>{r}</button>)}
      </div>
      <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Or type the reason" autoFocus />
      <div className="row gap-6" style={{ justifyContent: "flex-end", marginTop: 14 }}>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={() => onSave(reason.trim())}>Mark {stage === "not_fit" ? "not a fit" : "lost"}</button>
      </div>
    </LeModal>
  );
}

function EditDetailsModal({ p, onClose, onSaved }: any) {
  const api = leApi();
  // the contact as first + last; a prospect from before #26 (or the backfill) has only the full name, split here
  const whole = leSplitName(p.owner_name);
  const split = !!(p.contact_first_name || p.contact_last_name);
  const [f, setF] = React.useState<any>({
    business_name: p.business_name || "", website: p.website || "", phone: p.phone_display || "", email: p.email || "",
    contact_first_name: split ? p.contact_first_name || "" : whole.first, contact_last_name: split ? p.contact_last_name || "" : whole.last,
    street: p.street || "", city: p.city || "", state: p.state || "", zip: p.zip || "",
  });
  const [busy, setBusy] = React.useState<any>(false);
  const [err, setErr] = React.useState<any>("");
  const set = (k: string, v: any) => setF((x: any) => ({ ...x, [k]: v }));
  async function save(e?: any) {
    if (e) e.preventDefault();
    setBusy(true); setErr("");
    const fields = { ...f };
    if (fields.state === (p.state || "") && !leNormState(fields.state)) delete fields.state;   // a pre-check value left as it was
    try { const r = await api.update(p.id, { fields }); notify("Saved." + (r.prospect.enrich_status === "queued" && p.enrich_status !== "queued" ? " Reading the new website." : "")); onSaved(r.prospect); onClose(); }
    catch (x: any) { setErr(leErrText(x)); } finally { setBusy(false); }
  }
  const input = (k: string, label: string, props: any = {}) => <div style={{ minWidth: 0 }}><label style={lab} htmlFor={"le-ed-" + k}>{label}</label><input id={"le-ed-" + k} className="input" value={f[k]} onChange={(e) => set(k, e.target.value)} {...props} /></div>;
  return (
    <LeModal title="Edit details" onClose={onClose}>
      <form onSubmit={save}>
        {input("business_name", "Business name")}
        <div className="le-2col">{input("website", "Website", { inputMode: "url" })}{input("phone", "Phone", { inputMode: "tel" })}</div>
        <div className="le-2col">{input("contact_first_name", "Contact first name", { autoComplete: "off" })}{input("contact_last_name", "Last name", { autoComplete: "off" })}</div>
        {input("email", "Email", { inputMode: "email" })}
        {input("street", "Street")}
        <div className="le-2col" style={{ gridTemplateColumns: "1fr 160px 100px" }}>
          {input("city", "City")}
          <div style={{ minWidth: 0 }}><label style={lab} htmlFor="le-ed-state">State</label><LeStateSelect id="le-ed-state" value={f.state} onChange={(v: string) => set("state", v)} keep /></div>
          {input("zip", "ZIP")}
        </div>
        {err && <p style={{ color: "var(--red)", fontSize: 13, margin: "8px 0 0" }}>{err}</p>}
        <div className="row gap-6" style={{ justifyContent: "flex-end", marginTop: 14 }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>{busy ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </LeModal>
  );
}

/* ============================================================
   Prospect — one business
   ============================================================ */
const STYLE_LABEL: Record<string, string> = { "wood-privacy": "Wood privacy", vinyl: "Vinyl", aluminum: "Aluminum", "chain-link": "Chain link", pool: "Pool code" };

function AuditCard({ p, onEnrich, busy, onEdit }: any) {
  const en = p.enrichment || {};
  const audit = en.audit || null;
  const shots = en.screenshots || {};
  const status = p.enrich_status;
  const chip = status === "queued" ? <span className="chip amber" style={{ fontSize: 11 }}>Queued — reading soon</span>
    : status === "running" ? <span className="chip teal" style={{ fontSize: 11 }}><span className="le-spin" aria-hidden />Reading their site…</span>
    : status === "failed" ? <span className="chip" style={{ fontSize: 11, color: "var(--red)", borderColor: "var(--red)" }}>Couldn't read it</span>
    : status === "done" && p.enriched_at ? <span className="faint" style={{ fontSize: 11.5 }}>read {leTimeAgo(p.enriched_at)}</span> : null;
  const desktop = leSafeImg(shots.desktop), mobile = leSafeImg(shots.mobile);
  return (
    <LeSection title="Website audit" className="le-o-audit" right={<div className="row gap-6" style={{ alignItems: "center", flexWrap: "wrap" }}>{chip}{p.website && <button className="btn btn-outline btn-sm" disabled={busy || status === "running" || status === "queued"} onClick={onEnrich}><Icon name="undo" size={12} />{status === "done" || status === "failed" ? "Re-read website" : "Read website"}</button>}</div>}>
      {status === "failed" && p.enrich_error && <p style={{ color: "var(--red)", fontSize: 12.5, margin: "0 0 10px" }}>{String(p.enrich_error)}</p>}
      {!p.website ? (
        <div className="le-nosite">
          <b>No website.</b> That's the strongest signal there is — a preview site is the whole pitch.
          <div style={{ marginTop: 8 }}><button className="btn btn-outline btn-sm" onClick={onEdit}><Icon name="edit" size={12} />Add their website if you find one</button></div>
        </div>
      ) : !audit && !desktop && !mobile ? (
        <p className="muted" style={{ fontSize: 13, margin: 0 }}>{status === "queued" || status === "running" ? "Screenshots and the checklist land here in a minute or two." : "Not read yet. Read website takes screenshots and checks HTTPS, phones, a quote form, speed and the copyright year."}</p>
      ) : (
        <>
          {(desktop || mobile) && (
            <div className="le-shots">
              {desktop && <a href={desktop} target="_blank" rel="noreferrer noopener" className="le-shot le-shot-desktop" title="Their site on a computer"><img src={desktop} alt="Their current website on a computer" loading="lazy" /></a>}
              {mobile && <a href={mobile} target="_blank" rel="noreferrer noopener" className="le-shot le-shot-mobile" title="Their site on a phone"><img src={mobile} alt="Their current website on a phone" loading="lazy" /></a>}
            </div>
          )}
          {audit && Array.isArray(audit.checks) && audit.checks.length > 0 && (
            <div className="le-checks">
              {audit.checks.map((c: any, i: number) => (
                <div key={c.key || i} className="le-check">
                  <span className={"le-tick " + (c.pass ? "ok" : "bad")} aria-label={c.pass ? "pass" : "fail"}><Icon name={c.pass ? "check" : "x"} size={11} /></span>
                  <div style={{ minWidth: 0 }}><div style={{ fontSize: 13 }}>{String(c.label || c.key || "")}</div>{c.detail && <div className="faint" style={{ fontSize: 11.5 }}>{String(c.detail)}</div>}</div>
                </div>
              ))}
            </div>
          )}
          {audit && (audit.loadMs || audit.copyrightYear) && <div className="faint" style={{ fontSize: 12, marginTop: 8 }}>{[audit.loadMs ? "Loads in " + (audit.loadMs < 100 ? "under 0.1 s" : (audit.loadMs / 1000).toFixed(1) + " s") : "", audit.copyrightYear ? "© " + audit.copyrightYear : ""].filter(Boolean).join(" · ")}{en.finalUrl ? " · " + String(en.finalUrl) : ""}</div>}
        </>
      )}
    </LeSection>
  );
}

function FactsCard({ p }: any) {
  const en = p.enrichment || {};
  const f = en.facts || null;
  const g = en.google || null;
  if (!f && !g) return <LeSection title="What we read" className="le-o-facts"><p className="muted" style={{ fontSize: 13, margin: 0 }}>Nothing yet. Their logo, phone, towns, fence styles and job photos show up here once their website has been read.</p></LeSection>;
  const logo = f && leSafeImg(f.logo);
  const photos = ((f && f.jobPhotos) || []).map((x: any) => ({ src: leSafeImg(x && x.src), alt: String((x && x.alt) || "") })).filter((x: any) => x.src).slice(0, 24);
  const social = f && f.social && typeof f.social === "object" ? Object.keys(f.social).map((k) => [k, leSafeHref(f.social[k])]).filter((x) => x[1]) : [];
  const row = (k: string, v: any) => (v == null || v === "" ? null : <><span className="muted">{k}</span><span style={{ minWidth: 0, overflowWrap: "anywhere" }}>{v}</span></>);
  return (
    <LeSection title="What we read" className="le-o-facts" right={<span className="faint" style={{ fontSize: 11.5 }}>from their website{g ? " and Google" : ""}</span>}>
      {logo && <div className="le-logo"><img src={logo} alt={(f.name || p.business_name) + " logo"} loading="lazy" /></div>}
      <div className="le-kv">
        {f && row("Name", f.name)}
        {f && row("Phone", f.phone)}
        {f && row("Email", f.email)}
        {f && row("Address", [f.street, f.city, f.state, f.zip].filter(Boolean).join(", "))}
        {f && row("Owner", f.ownerName)}
        {f && row("License", f.license)}
        {f && row("Founded", f.yearFounded)}
        {g && row("Google", <>{g.rating != null ? <b>{Number(g.rating).toFixed(1)}★</b> : "—"} {g.reviews != null ? "(" + g.reviews + " reviews)" : ""} {leSafeHref(g.mapsUrl) && <a href={leSafeHref(g.mapsUrl) as string} target="_blank" rel="noreferrer noopener">Maps</a>}</>)}
      </div>
      {f && Array.isArray(f.serviceAreas) && f.serviceAreas.length > 0 && <><div className="kicker" style={{ margin: "12px 0 6px" }}>Service areas</div><div className="row gap-4" style={{ flexWrap: "wrap" }}>{f.serviceAreas.slice(0, 30).map((a: any) => <span key={String(a)} className="chip" style={{ fontSize: 11 }}>{String(a)}</span>)}</div></>}
      {f && Array.isArray(f.styles) && f.styles.length > 0 && <><div className="kicker" style={{ margin: "12px 0 6px" }}>Fence styles</div><div className="row gap-4" style={{ flexWrap: "wrap" }}>{f.styles.map((s: any) => <span key={String(s)} className="chip teal" style={{ fontSize: 11 }}>{STYLE_LABEL[s] || String(s)}</span>)}</div></>}
      {photos.length > 0 && <><div className="kicker" style={{ margin: "12px 0 6px" }}>Their job photos · {photos.length}</div><div className="le-photos">{photos.map((ph: any, i: number) => <a key={i} href={ph.src} target="_blank" rel="noreferrer noopener"><img src={ph.src} alt={ph.alt || "Job photo"} loading="lazy" /></a>)}</div></>}
      {social.length > 0 && <div className="row gap-6" style={{ flexWrap: "wrap", marginTop: 10, fontSize: 12.5 }}>{social.map(([k, u]: any) => <a key={k} href={u} target="_blank" rel="noreferrer noopener" className="chip" style={{ fontSize: 11 }}>{k}</a>)}</div>}
      {f && f.about && <p className="muted" style={{ fontSize: 12.5, margin: "10px 0 0", lineHeight: 1.5 }}>{String(f.about).slice(0, 400)}{String(f.about).length > 400 ? "…" : ""}</p>}
    </LeSection>
  );
}

function SiteCardView({ p, site, onChange }: any) {
  const api = leApi();
  const [busy, setBusy] = React.useState<any>(null);
  const [qr, setQr] = React.useState<any>(false);
  const facts = p.enrichment && p.enrichment.facts;
  async function act(kind: string, fn: () => Promise<any>, ok: string) {
    setBusy(kind);
    try { const r = await fn(); notify(ok, { kind: "success" }); onChange && onChange(r); }
    catch (e: any) { notify(e && e.code === "not_enriched" ? "Read their website first — the site is built from what's on it." : leErrText(e), { kind: "error" }); }
    finally { setBusy(null); }
  }
  const build = () => act("build", () => api.buildSite(p.id), site ? "Rebuilt from what we read." : "Preview site built.");
  async function rebuild() { if (await confirmDialog({ title: "Rebuild the preview?", body: "It's rebuilt from what we read off their website. The link stays the same." , confirmLabel: "Rebuild" })) build(); }
  async function del() { if (await confirmDialog({ title: "Delete the preview site?", body: "The link stops working and its copied photos are removed. You can build it again later.", confirmLabel: "Delete", danger: true })) act("delete", () => api.deleteSite(p.id), "Preview site deleted."); }
  function copy() {
    const u = site && site.previewUrl; if (!u) return;
    const done = () => notify("Link copied.", { kind: "success" });
    try { navigator.clipboard.writeText(u).then(done, () => window.prompt("Copy the link", u)); } catch (e) { window.prompt("Copy the link", u); }
  }
  if (!site) {
    return (
      <LeSection title="Preview site" className="le-o-site">
        <p className="muted" style={{ fontSize: 13, margin: "0 0 12px" }}>A new website built for {p.business_name} from their own logo, towns and photos — on a private link, ready to send. It takes about a minute.</p>
        <button className="btn btn-primary btn-sm" disabled={!facts || !!busy} onClick={build}><Icon name="bolt" size={13} />{busy === "build" ? "Building…" : "Build site"}</button>
        {!facts && <div className="faint" style={{ fontSize: 12, marginTop: 6 }}>{p.website ? "Read their website first." : "Read their website first — add it under Edit details."}</div>}
      </LeSection>
    );
  }
  const expired = site.expired === true || (site.expiresAt && Date.parse(site.expiresAt) <= Date.now());
  const expSoon = !expired && site.expiresAt && Date.parse(site.expiresAt) - Date.now() < 7 * 86400000;
  // the link we send (Copy, QR) vs the one we open: ownerPreviewUrl carries the
  // owner's key, so our own looks never count as a view or fire the hot alert
  const url = leSafeHref(site.previewUrl);
  const openUrl = leSafeHref(site.ownerPreviewUrl) || url;
  return (
    <LeSection title="Preview site" className="le-o-site" right={site.claimedAt ? <span className="chip green" style={{ fontSize: 11 }}>Claimed {leTimeAgo(site.claimedAt)}</span> : expired ? <span className="chip" style={{ fontSize: 11, color: "var(--red)", borderColor: "var(--red)" }}>Expired</span> : null}>
      {expired && !site.claimedAt && <p style={{ fontSize: 12.5, color: "var(--amber)", margin: "0 0 10px" }}>This preview has ended — the link shows “This preview has ended”. Extend it to bring it back.</p>}
      {url ? (
        <div className="le-link">
          <code title={url}>{url.replace(/^https?:\/\//, "")}</code>
          <div className="row gap-6" style={{ flexWrap: "wrap" }}>
            <button className="btn btn-outline btn-sm" onClick={copy}><Icon name="clipboard" size={12} />Copy</button>
            <button className="btn btn-outline btn-sm" aria-expanded={qr} onClick={() => setQr(!qr)}>QR</button>
            {openUrl && <a className="btn btn-outline btn-sm" href={openUrl} target="_blank" rel="noreferrer noopener" title={site.ownerPreviewUrl ? "Opens as you — your views don't count or alert" : undefined}><Icon name="chevright" size={12} />Open</a>}
          </div>
        </div>
      ) : <p className="faint" style={{ fontSize: 12.5 }}>No link yet.</p>}
      {qr && url && <div className="le-qr"><QrCode url={url} size={140} /><span>Hold it up to their phone, or print it on the postcard.</span></div>}
      <div className="le-site-stats">
        <div><div className="display tnum" style={{ fontSize: 22 }}>{site.views || 0}</div><div className="faint" style={{ fontSize: 11.5 }}>views</div></div>
        <div><div style={{ fontWeight: 650, fontSize: 13.5 }}>{site.lastViewedAt ? "last viewed " + leTimeAgo(site.lastViewedAt) : "not opened yet"}</div><div className="faint" style={{ fontSize: 11.5 }}>{site.builtAt ? "built " + leTimeAgo(site.builtAt) : ""}</div></div>
        <div><div style={{ fontWeight: 650, fontSize: 13.5, color: expired ? "var(--red)" : expSoon ? "var(--amber)" : undefined }}>{site.expiresAt ? (expired ? "expired " : "expires ") + shortDate(site.expiresAt) : "no expiry"}</div><div className="faint" style={{ fontSize: 11.5 }}>{site.expiresAt ? leTimeAgo(site.expiresAt) : ""}</div></div>
      </div>
      <div className="row gap-6" style={{ flexWrap: "wrap", marginTop: 12 }}>
        <button className="btn btn-outline btn-sm" disabled={!!busy} onClick={() => act("extend", () => api.extendSite(p.id, 30), "Extended 30 days.")}><Icon name="clock" size={12} />{busy === "extend" ? "Extending…" : "Extend 30 days"}</button>
        <button className="btn btn-outline btn-sm" disabled={!!busy || !facts} onClick={rebuild}><Icon name="bolt" size={12} />{busy === "build" ? "Rebuilding…" : "Rebuild"}</button>
        <button className="btn btn-ghost btn-sm" disabled={!!busy} onClick={del} style={{ color: "var(--red)" }}><Icon name="trash" size={12} />Delete</button>
      </div>
    </LeSection>
  );
}

function Timeline({ events }: any) {
  const list = events || [];
  return (
    <LeSection title="Timeline" className="le-o-timeline" right={<span className="faint" style={{ fontSize: 12 }}>{list.length} event{list.length === 1 ? "" : "s"}</span>}>
      {list.length === 0 ? <span className="muted" style={{ fontSize: 13 }}>Nothing recorded yet.</span> : (
        <div className="le-timeline">
          {list.map((ev: any) => {
            const v = leEventView(ev);
            return (
              <div key={ev.id || ev.at + ev.type} className={"le-ev le-ev-" + ev.type}>
                <span className="le-ev-ico" aria-hidden><Icon name={v.icon} size={12} /></span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{v.title}</div>
                  {v.detail && <div style={{ fontSize: 12.5, color: "var(--fg-2)", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{v.detail}</div>}
                  <div className="faint" style={{ fontSize: 11 }}>{dateTime(ev.at)} · {leTimeAgo(ev.at)}{ev.actor && ev.actor !== "system" ? "" : " · automatic"}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </LeSection>
  );
}

function ProspectPage({ params, go }: any) {
  const id = params && params.id;
  const { api, live, data, setData, denied, migration, err, load } = useLeLoad((a) => a.get(id), [id]);
  const [notes, setNotes] = React.useState<any>(null);
  const [tag, setTag] = React.useState<any>("");
  const [next, setNext] = React.useState<any>(null);
  const [modal, setModal] = React.useState<any>(null);
  const [busy, setBusy] = React.useState<any>(null);
  const [touch, setTouch] = React.useState<any>("");
  const [showScore, setShowScore] = React.useState<any>(false);
  const p = data && data.prospect;
  React.useEffect(() => { if (p) { setNotes((n: any) => (n === null ? p.notes || "" : n)); setNext((n: any) => (n === null ? { text: p.next_step || "", date: toDateInput(p.next_step_at) } : n)); } }, [p]);
  React.useEffect(() => { setNotes(null); setNext(null); }, [id]);
  const reading = !!(p && (p.enrich_status === "queued" || p.enrich_status === "running"));
  usePoll(load, 15_000, reading);
  if (!id) return <div className="page fade-up"><p className="muted">No prospect picked.</p><button className="btn btn-ghost btn-sm" onClick={() => go("leadengine")}>← Pipeline</button></div>;
  const guard = <LeGuard live={live} denied={denied} migration={migration} />;
  if (!live || denied || migration) return guard;
  if (err && !data) return <div className="page fade-up"><button className="btn btn-ghost btn-sm" onClick={() => go("leadengine")} style={{ marginBottom: 8 }}>← Pipeline</button><div className="card pad"><EmptyState icon="target" title="Couldn't open this prospect" sub={err} /></div></div>;
  if (!data) return <div className="page fade-up"><p className="muted" style={{ textAlign: "center", padding: 24 }}>Loading…</p></div>;

  const setP = (np: any) => { if (np) setData((d: any) => ({ ...d, prospect: { ...d.prospect, ...np } })); };
  async function patch(body: any, ok?: string) {
    setBusy("patch");
    try { const r = await api.update(p.id, body); setP(r.prospect); if (ok) notify(ok, { kind: "success" }); load(); return r; }
    catch (e: any) { notify(leErrText(e), { kind: "error" }); return null; }
    finally { setBusy(null); }
  }
  function changeStage(s: string) {
    if (s === p.stage) return;
    if (s === "lost" || s === "not_fit") { setModal({ kind: "lost", stage: s }); return; }
    patch({ stage: s }, "Moved to " + leStageLabel(s) + ".");
  }
  async function enrich() {
    setBusy("enrich");
    try { await api.enrich(p.id); notify("Reading their website — screenshots and the checklist land in a minute or two."); setP({ enrich_status: "queued" }); load(); }
    catch (e: any) { notify(e && e.status === 409 ? "Already reading it." : leErrText(e), { kind: e && e.status === 409 ? "info" : "error" }); }
    finally { setBusy(null); }
  }
  async function logTouch(type: string, text?: string) {
    try { const r = await api.log(p.id, { type, text: text || undefined }); setP(r.prospect); load(); return r; }
    catch (e: any) { notify(leErrText(e), { kind: "error" }); return null; }
  }
  async function remove() {
    const ok = await confirmDialog({ title: "Delete " + p.business_name + "?", body: "The prospect, its whole timeline and its preview site are deleted. This can't be undone, and a later import won't bring them back.\nTo stop working them but keep the history, mark them Lost or Not a fit instead.", confirmLabel: "Delete", danger: true });
    if (!ok) return;
    try { await api.remove(p.id); notify("Deleted."); go("leadengine"); } catch (e: any) { notify(leErrText(e), { kind: "error" }); }
  }
  const saveNotes = () => { if (notes !== null && notes !== (p.notes || "")) patch({ notes }, "Notes saved."); };
  const addTag = () => { const t = tag.trim(); if (!t) return; setTag(""); patch({ tags: [...(p.tags || []), t] }); };
  const nextDirty = next && (next.text !== (p.next_step || "") || next.date !== toDateInput(p.next_step_at));
  const tel = telOf(p);
  const website = leSafeHref(p.website);
  const g = (p.enrichment && p.enrichment.google) || null;
  const maps = leSafeHref(g && g.mapsUrl) || "https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent([p.business_name, p.street, p.city, p.state].filter(Boolean).join(" ")) + (p.place_id ? "&query_place_id=" + encodeURIComponent(p.place_id) : "");
  const breakdown = leBreakdownLines(p.score_breakdown);

  return (
    <div className="page fade-up le-page" style={{ maxWidth: 1400 }}>
      <button className="btn btn-ghost btn-sm" onClick={() => go("leadengine")} style={{ marginBottom: 8 }}>← Pipeline</button>
      <div className="le-phead">
        <div style={{ minWidth: 0 }}>
          <div className="row gap-8" style={{ alignItems: "center", flexWrap: "wrap" }}>
            <h1 className="display" style={{ fontSize: 28, margin: 0, overflowWrap: "anywhere" }}>{p.business_name}</h1>
            <button className="le-score-btn" onClick={() => setShowScore(!showScore)} aria-expanded={showScore} title="What the score is made of"><LeScore score={p.score} breakdown={p.score_breakdown} big /></button>
            {p.do_not_contact && <span className="chip" style={{ fontSize: 11, color: "var(--red)", borderColor: "var(--red)" }}>Do not contact</span>}
          </div>
          <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>{[place(p), leContactName(p), LE_SOURCES[p.source] ? LE_SOURCES[p.source] + (p.source_detail ? " (" + leSourceDetail(p) + ")" : "") : p.source, p.external_id ? "ID " + p.external_id : "", "added " + leTimeAgo(p.created_at)].filter(Boolean).join(" · ")}</div>
          {showScore && (
            <div className="le-breakdown">
              <b>Opportunity score {p.score == null ? "— not scored yet" : p.score + "/100"}</b>
              {breakdown.length ? breakdown.map((l, i) => <div key={i}>{l}</div>) : <div className="faint">The score comes from reading their website and Google profile: no website +35; no HTTPS, not mobile, no quote form +10 each; 4.3★+ with 20+ reviews +20.</div>}
            </div>
          )}
        </div>
        <div className="le-pactions">
          <select className="select" value={p.stage} onChange={(e) => changeStage(e.target.value)} aria-label="Stage" disabled={busy === "patch"} style={{ width: 170, padding: "7px 34px 7px 12px" }}>
            {LE_STAGES.map(([s, l]) => <option key={s} value={s}>{l}</option>)}
          </select>
          {website && <a className="btn btn-outline btn-sm" href={website} target="_blank" rel="noreferrer noopener"><Icon name="building" size={13} />Website</a>}
          <a className="btn btn-outline btn-sm" href={maps} target="_blank" rel="noreferrer noopener"><Icon name="mappin" size={13} />Google Maps</a>
          {tel && <a className="btn btn-primary btn-sm" href={tel} onClick={() => setTimeout(() => setModal({ kind: "call" }), 400)}><Icon name="phone" size={13} />Call {p.phone_display || ""}</a>}
          {p.email && <a className="btn btn-outline btn-sm" href={"mailto:" + encodeURIComponent(p.email).replace(/%40/g, "@")} onClick={() => logTouch("email")}><Icon name="mail" size={13} />Email</a>}
        </div>
      </div>
      {p.stage === "lost" && p.lost_reason && <p className="muted" style={{ fontSize: 13, margin: "0 0 10px" }}>Lost: {p.lost_reason}</p>}
      {err && <p style={{ color: "var(--red)", fontSize: 13 }}>{err}</p>}

      <div className="le-grid">
        <div className="le-col">
          <AuditCard p={p} busy={busy === "enrich"} onEnrich={enrich} onEdit={() => setModal({ kind: "edit" })} />
          <SiteCardView p={p} site={data.site} onChange={() => load()} />
          <OutreachPanel p={p} onChange={() => load()} />
          <FactsCard p={p} />
        </div>
        <div className="le-col">
          <LeSection title="Log a touch" className="le-o-log">
            <div className="kicker" style={{ marginBottom: 6 }}>Call outcome</div>
            <OutcomeButtons p={p} text={touch} onLogged={(np: any) => { setP(np); setTouch(""); load(); }} />
            <textarea className="textarea" rows={2} value={touch} onChange={(e) => setTouch(e.target.value)} placeholder="What happened — goes with the call, note or DM" style={{ width: "100%", marginTop: 10 }} aria-label="Touch note" />
            <div className="row gap-6" style={{ marginTop: 8, flexWrap: "wrap" }}>
              <button className="btn btn-outline btn-sm" disabled={!touch.trim()} onClick={async () => { if (await logTouch("note", touch)) { setTouch(""); notify("Note logged.", { kind: "success" }); } }}><Icon name="pen" size={12} />Log note</button>
              <button className="btn btn-outline btn-sm" onClick={async () => { if (await logTouch("dm", touch)) { setTouch(""); notify("DM logged.", { kind: "success" }); } }}><Icon name="chat" size={12} />Log DM</button>
              <button className="btn btn-ghost btn-sm" onClick={async () => { if (await logTouch("email", touch)) { setTouch(""); notify("Email logged.", { kind: "success" }); } }}><Icon name="mail" size={12} />Log email</button>
            </div>
          </LeSection>

          <LeSection title="Next step" className="le-o-next">
            <div className="le-next">
              <input className="input" value={(next && next.text) || ""} onChange={(e) => setNext((n: any) => ({ ...(n || {}), text: e.target.value }))} placeholder="Call Tuesday about the preview" aria-label="Next step" />
              <input className="input" type="date" value={(next && next.date) || ""} onChange={(e) => setNext((n: any) => ({ ...(n || {}), date: e.target.value }))} aria-label="Next step date" />
              <button className="btn btn-primary btn-sm" disabled={!nextDirty || busy === "patch"} onClick={() => patch({ next_step: next.text, next_step_at: next.date || null }, "Next step saved.")}>Save</button>
            </div>
            {p.next_step_at && <div style={{ fontSize: 12, marginTop: 6, color: leOverdue(p.next_step_at) ? "var(--red)" : "var(--fg-3)" }}>{leOverdue(p.next_step_at) ? "Overdue — was due " : "Due "}{shortDate(p.next_step_at)} ({leTimeAgo(p.next_step_at)})</div>}
          </LeSection>

          <LeSection title="Notes & tags" className="le-o-notes">
            <div className="row gap-4" style={{ flexWrap: "wrap", marginBottom: 8 }}>
              {(p.tags || []).map((t: string) => (
                <span key={t} className="chip" style={{ fontSize: 11.5 }}>{t}<button aria-label={"Remove " + t} style={{ border: 0, background: "none", cursor: "pointer", padding: "0 0 0 4px", color: "var(--fg-3)" }} onClick={() => patch({ tags: (p.tags || []).filter((x: string) => x !== t) })}>×</button></span>
              ))}
              <input className="input" placeholder="add tag (hot, vinyl, expo…)" value={tag} onChange={(e) => setTag(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addTag(); }} onBlur={addTag} style={{ width: 200, fontSize: 12, padding: "5px 10px" }} aria-label="Add tag" />
            </div>
            <textarea className="textarea" rows={5} placeholder="Who the owner is, what they said, what they care about… (saves when you click away)" value={notes ?? ""} onChange={(e) => setNotes(e.target.value)} onBlur={saveNotes} style={{ width: "100%" }} aria-label="Notes" />
            <div className="row between" style={{ marginTop: 10, gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <label className="row gap-6" style={{ fontSize: 12.5, alignItems: "center", cursor: "pointer" }}>
                <input type="checkbox" checked={!!p.do_not_contact} onChange={(e) => patch({ do_not_contact: e.target.checked }, e.target.checked ? "Marked do-not-contact — their email, website and phone are suppressed." : "Do-not-contact removed.")} /> Do not contact (they asked)
              </label>
              <div className="row gap-6">
                <button className="btn btn-ghost btn-sm" onClick={() => setModal({ kind: "edit" })}><Icon name="edit" size={12} />Edit details</button>
                <button className="btn btn-ghost btn-sm" onClick={remove} style={{ color: "var(--red)" }}><Icon name="trash" size={12} />Delete</button>
              </div>
            </div>
          </LeSection>

          <Timeline events={data.events} />
        </div>
      </div>

      {modal && modal.kind === "lost" && <LostReasonModal p={p} stage={modal.stage} onClose={() => setModal(null)} onSave={async (reason: string) => { setModal(null); await patch({ stage: modal.stage, lost_reason: reason || null }, "Marked " + leStageLabel(modal.stage).toLowerCase() + "."); }} />}
      {modal && modal.kind === "call" && <CallLogModal p={p} onClose={() => setModal(null)} onLogged={(np: any) => { setP(np); load(); }} />}
      {modal && modal.kind === "edit" && <EditDetailsModal p={p} onClose={() => setModal(null)} onSaved={(np: any) => { setP(np); load(); }} />}
    </div>
  );
}

/* ============================================================
   Find prospects (Google Places — workstream B's /find)
   ============================================================ */
function LeadFindPage({ go }: any) {
  const api = leApi();
  const live = !!(api && api.enabled && api.enabled());
  const [q, setQ] = useLocalPref("le_find_q", "");
  // the lead filters. Google can't search for "no website" or "few reviews", so
  // with either on the server reads every page Google has (up to 60 a phrasing)
  // and keeps the ones that match; "wide" asks the same town three ways.
  const [onlyNoSite, setOnlyNoSite] = useLocalPref("le_find_nosite", "");
  const [onlyFew, setOnlyFew] = useLocalPref("le_find_few", "");
  const [under, setUnder] = useLocalPref("le_find_under", "10");
  const [wide, setWide] = useLocalPref("le_find_wide", "");
  const filterOn = !!onlyNoSite || !!onlyFew;
  const deep = filterOn || !!wide;
  const [res, setRes] = React.useState<any>(null);
  const [sel, setSel] = React.useState<any>(() => new Set());
  const [added, setAdded] = React.useState<any>(() => new Set());
  const [busy, setBusy] = React.useState<any>(null);
  const [err, setErr] = React.useState<any>("");
  const [noKey, setNoKey] = React.useState<any>(false);
  const [denied, setDenied] = React.useState<any>(false);
  const [migration, setMigration] = React.useState<any>(false);
  const flows = useAddFlows(go);
  const narrow = useNarrowScreen(640);
  React.useEffect(() => {
    if (!live) return;
    api.list({ limit: 1 }).then((d: any) => { if (d && d.setup && d.setup.placesKey === false) setNoKey(true); })
      .catch((e: any) => { if (e && e.status === 403) setDenied(true); else if (leIsMigrationError(e)) setMigration(true); });
  }, [live]);
  // changing a lead filter re-runs the search that's on screen (never on first paint)
  const firstPaint = React.useRef(true);
  React.useEffect(() => { if (firstPaint.current) { firstPaint.current = false; return; } if (res) search(false); }, [onlyNoSite, onlyFew, under, wide]);
  const guard = <LeGuard live={live} denied={denied} migration={migration} />;
  if (!live || denied || migration) return guard;

  async function search(more = false) {
    const parsed = leParseFindQuery(q);
    if (!parsed.query && !parsed.city) return;
    setBusy(more ? "more" : "search"); setErr("");
    try {
      const body: any = { ...parsed };
      if (filterOn) body.filter = { noWebsite: !!onlyNoSite, reviewsUnder: onlyFew ? Number(under) || 10 : null };
      if (deep) body.deep = true;
      if (wide) body.variants = true;
      if (more && res && res.nextPageToken && !deep) body.pageToken = res.nextPageToken;
      const r = await api.find(body);
      setNoKey(false);
      setRes((cur: any) => (more && cur
        ? { results: [...cur.results, ...(r.results || [])], nextPageToken: r.nextPageToken || null, summary: leMergeSummary(cur.summary, r.summary) }
        : { results: r.results || [], nextPageToken: r.nextPageToken || null, summary: r.summary || null }));
      if (!more) setSel(new Set());
    } catch (e: any) {
      if (e && e.code === "places_key_missing") setNoKey(true);
      else if (e && e.status === 403) setDenied(true);
      else setErr(leErrText(e));
    } finally { setBusy(null); }
  }
  const results: any[] = leSortFound((res && res.results) || [], { noSite: !!onlyNoSite, lowRev: !!onlyFew });
  const selectable = results.filter((r) => !r.prospectId && !added.has(r.place_id));
  const toggle = (id: string) => setSel((s: any) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  async function addSelected() {
    const ids = [...sel];
    if (!ids.length) return;
    setBusy("add");
    try {
      const r = await api.findAdd(ids);
      notify("Added " + (r.created || 0) + " to the pipeline" + (r.duplicates ? " · " + r.duplicates + " were already there" : "") + ". Their websites are being read now.", { kind: "success" });
      setAdded((a: any) => new Set([...a, ...ids])); setSel(new Set());
    } catch (e: any) { notify(leErrText(e), { kind: "error" }); } finally { setBusy(null); }
  }
  const noSiteCount = results.filter((r) => !r.website).length;
  const inPipeline = (r: any) => r.prospectId || added.has(r.place_id);
  const few = (r: any) => ((r.signals || []).includes("few_reviews") ? <span className="le-few-badge" title={"Under " + ((res && res.summary && res.summary.reviewsUnder) || 10) + " Google reviews — the Google profile pitch"}>few reviews</span> : null);
  const rating = (r: any) => (r.rating != null ? <span style={{ whiteSpace: "nowrap" }}><b>{Number(r.rating).toFixed(1)}</b><span style={{ color: "var(--amber)" }}>★</span> <span className="faint">({r.reviews || 0})</span> {few(r)}</span> : <span className="faint">no rating {few(r)}</span>);
  const site = (r: any) => (r.website ? <a href={leSafeHref(r.website) || undefined} target="_blank" rel="noreferrer noopener" style={{ fontSize: 12.5, overflowWrap: "anywhere" }} onClick={(e) => e.stopPropagation()}>{String(r.website).replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "").slice(0, 40)}</a> : <span className="le-nosite-badge">No website</span>);
  const status = (r: any) => (r.prospectId ? <button className="btn btn-ghost btn-sm" style={{ padding: "2px 8px" }} onClick={() => go("prospect", { id: r.prospectId })}>In pipeline →</button> : added.has(r.place_id) ? <span className="chip green" style={{ fontSize: 11 }}>Added</span> : null);

  return (
    <div className="page fade-up le-page" style={{ maxWidth: 1200 }}>
      <div className="row between le-head" style={{ marginBottom: 10, flexWrap: "wrap", gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <h1 className="display" style={{ fontSize: 28, margin: 0 }}>Find prospects</h1>
          <p className="muted" style={{ margin: "4px 0 0" }}>Search Google for fence contractors in a town. A good rating and no website is the best prospect there is.</p>
        </div>
        <div className="row gap-6 le-actions" style={{ flexWrap: "wrap" }}>
          <button className="btn btn-outline btn-sm" onClick={() => flows.open("link")}><Icon name="arrow" size={13} />Paste a link</button>
          <button className="btn btn-outline btn-sm" onClick={() => flows.open("csv")}><Icon name="upload" size={13} />Import CSV</button>
        </div>
      </div>
      <LeTabs route="leadengine-find" go={go} />

      {noKey && (
        <div className="card pad le-setup" style={{ marginBottom: 14 }}>
          <div className="kicker" style={{ color: "var(--amber)" }}>Google search is off</div>
          <h3 className="display" style={{ margin: "4px 0 8px", fontSize: 18 }}>Turn on Google search</h3>
          <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13.5, lineHeight: 1.75 }}>
            <li>In Google Cloud, enable <b>Places API (New)</b> on the Fence Works project.</li>
            <li>Create an API key restricted to the <b>server's IP</b> and to Places API. Set a budget alert.</li>
            <li>Put it in the server <code>.env</code> as <code>GOOGLE_PLACES_KEY=…</code></li>
            <li>Run <code>pm2 restart all --update-env</code>.</li>
          </ol>
          <p className="muted" style={{ fontSize: 13, margin: "10px 0 0" }}>Until then, <a href="#" onClick={(e) => { e.preventDefault(); flows.open("link"); }}>paste a link</a> or <a href="#" onClick={(e) => { e.preventDefault(); flows.open("csv"); }}>import a CSV</a> — both work now.</p>
        </div>
      )}

      <form className="le-find" onSubmit={(e) => { e.preventDefault(); search(false); }}>
        <input className="input" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="fence contractor in Green Bay, WI — or just Green Bay, WI" aria-label="Search Google for fence contractors" />
        <button className="btn btn-primary" type="submit" disabled={!!busy || !String(q).trim()}><Icon name="search" size={15} />{busy === "search" ? (deep ? "Reading every page…" : "Searching…") : "Search"}</button>
      </form>
      <div className="le-find-filters" role="group" aria-label="Lead filters">
        <span className="faint" style={{ fontSize: 12 }}>Show only</span>
        <button type="button" className={"chip" + (onlyNoSite ? " amber" : "")} aria-pressed={!!onlyNoSite} style={{ cursor: "pointer" }} onClick={() => setOnlyNoSite(onlyNoSite ? "" : "1")} title="Businesses Google lists with no website — the website pitch">No website</button>
        <button type="button" className={"chip" + (onlyFew ? " violet" : "")} aria-pressed={!!onlyFew} style={{ cursor: "pointer" }} onClick={() => setOnlyFew(onlyFew ? "" : "1")} title="Businesses with few Google reviews — the Google profile pitch">Few reviews</button>
        {onlyFew && <select className="select" value={under} onChange={(e) => setUnder(e.target.value)} aria-label="Fewer than this many Google reviews">{[3, 5, 10, 15, 25, 50].map((n) => <option key={n} value={String(n)}>under {n}</option>)}</select>}
        <span className="le-chip-sep" aria-hidden />
        <button type="button" className={"chip" + (wide ? " teal" : "")} aria-pressed={!!wide} style={{ cursor: "pointer" }} onClick={() => setWide(wide ? "" : "1")} title="Also ask Google for “fence company” and “fence installation” in the same town — each phrasing ranks (and caps at 60) differently, so the three together find more">Widen: 3 phrasings</button>
        <span className="faint" style={{ fontSize: 11.5 }}>{deep ? "Reads every page Google has — up to 60 a phrasing, " + (wide ? "12" : "3") + " Google requests at most" : "One page of 20 at a time"}</span>
      </div>
      {err && <p style={{ color: "var(--red)", fontSize: 13 }}>{err}</p>}

      {res && (results.length === 0 ? <div className="card pad"><EmptyState icon="search" title={filterOn ? "No leads like that here" : "Nothing found"} sub={filterOn && res.summary && res.summary.scanned ? "Google listed " + res.summary.scanned + " fence businesses for this search and " + (onlyNoSite && onlyFew ? "every one has a website and " + under + "+ reviews" : onlyNoSite ? "every one has a website" : "every one has " + under + "+ reviews") + ". Try the next town over, widen to three phrasings, or turn the filter off." : "Try the next town over, or a wider search like “fence company near Appleton, WI”."} /></div> : (
        <>
          <div className="row between" style={{ gap: 8, flexWrap: "wrap", margin: "4px 0 10px", alignItems: "center" }}>
            <div style={{ fontSize: 13 }}>
              {res.summary ? leFindSummary(res.summary, results.filter(inPipeline).length) : <><b>{results.length}</b> businesses · <span style={{ color: "var(--amber)", fontWeight: 700 }}>{noSiteCount} with no website</span> · {results.filter(inPipeline).length} already in your pipeline</>}
              {/* Google ran dry on this one phrasing (a small town pads out with far-away matches): the other two phrasings usually find more */}
              {res.summary && !wide && Number(res.summary.queries) <= 1 && !res.nextPageToken && Number(res.summary.scanned) < 20 && (
                <div className="faint" style={{ fontSize: 12, marginTop: 2 }}>Google ran out of matches for this phrasing. <button type="button" className="le-link-btn" onClick={() => setWide("1")}>Widen: 3 phrasings</button> asks the same town two more ways and usually finds more.</div>
              )}
            </div>
            <div className="row gap-6">
              <button className="btn btn-ghost btn-sm" disabled={!selectable.length} onClick={() => setSel(sel.size === selectable.length ? new Set() : new Set(selectable.map((r) => r.place_id)))}>{sel.size === selectable.length && selectable.length ? "Clear" : "Select all new"}</button>
              <button className="btn btn-primary btn-sm" disabled={!sel.size || !!busy} onClick={addSelected}><Icon name="plus" size={13} />{busy === "add" ? "Adding…" : "Add " + sel.size + " to pipeline"}</button>
            </div>
          </div>
          {narrow ? (
            <div className="le-cards">
              {results.map((r) => (
                <ListRow key={r.place_id} muted={!!inPipeline(r)} onClick={inPipeline(r) ? undefined : () => toggle(r.place_id)}
                  leading={<input type="checkbox" checked={sel.has(r.place_id)} disabled={!!inPipeline(r)} onChange={() => toggle(r.place_id)} aria-label={"Select " + r.name} />}
                  title={r.name} sub={<span>{rating(r)} · {r.address || ""}</span>} trailing={<span className="row gap-6" style={{ alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>{site(r)}{status(r)}</span>} />
              ))}
            </div>
          ) : (
            <div className="card" style={{ padding: 0 }}><div style={{ overflowX: "auto" }}>
              <table className="le-table">
                <thead><tr><th style={{ width: 34 }}><input type="checkbox" aria-label="Select all new" checked={!!selectable.length && sel.size === selectable.length} onChange={() => setSel(sel.size === selectable.length ? new Set() : new Set(selectable.map((x) => x.place_id)))} /></th><th>Business</th><th>Google rating</th><th>Website</th><th>Phone</th><th></th></tr></thead>
                <tbody>
                  {results.map((r) => (
                    <tr key={r.place_id} className={inPipeline(r) ? "le-dim" : "le-click"} onClick={() => !inPipeline(r) && toggle(r.place_id)}>
                      <td><input type="checkbox" checked={sel.has(r.place_id)} disabled={!!inPipeline(r)} onChange={() => toggle(r.place_id)} onClick={(e) => e.stopPropagation()} aria-label={"Select " + r.name} /></td>
                      <td style={{ minWidth: 220 }}><div style={{ fontWeight: 650 }}>{r.name}</div><div className="faint" style={{ fontSize: 11.5 }}>{r.address || ""}{r.businessStatus && r.businessStatus !== "OPERATIONAL" ? " · " + String(r.businessStatus).toLowerCase().replace(/_/g, " ") : ""}</div></td>
                      <td>{rating(r)}</td>
                      <td>{site(r)}</td>
                      <td style={{ fontSize: 12.5, whiteSpace: "nowrap" }}>{r.phone || <span className="faint">—</span>}</td>
                      <td style={{ textAlign: "right" }}>{status(r)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div></div>
          )}
          <div className="row between" style={{ marginTop: 10, gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <span className="faint" style={{ fontSize: 11.5 }}>Results from Google{res.summary && res.summary.deep ? " — every page it had" : ""}. Only the place ID is kept for good; ratings refresh or drop after 30 days.</span>
            {res.nextPageToken && !deep && <button className="btn btn-outline btn-sm" disabled={!!busy} onClick={() => search(true)}>{busy === "more" ? "Loading…" : "Load more"}</button>}
          </div>
        </>
      ))}
      {!res && !noKey && <div className="card pad" style={{ textAlign: "center" }}><EmptyState icon="search" title="Search a town" sub="Try “fence contractor in Green Bay, WI”. Turn on No website or Few reviews to pull just those leads — the search then reads every page Google has. Businesses already in your pipeline are greyed out." /></div>}
      {flows.modal}
    </div>
  );
}

/* ============================================================
   Today — who to call, in order
   ============================================================ */
function LeadTodayPage({ go }: any) {
  const { live, data, denied, migration, err, load } = useLeLoad((a) => a.today(), []);
  const ot = useOutreachToday();   // the Website pitch's call tasks + replies waiting (outreach, #27)
  const otLoad = ot.load;
  const reloadAll = React.useCallback(() => { load(); otLoad(); }, [load, otLoad]);
  usePoll(reloadAll, 30_000, live && !denied && !migration);
  const narrow = useNarrowScreen(640);
  const guard = <LeGuard live={live} denied={denied} migration={migration} />;
  if (!live || denied || migration) return guard;
  const hot: any[] = (data && data.lookingNow) || [];
  // ranked: looking now first, then replies waiting, then pitch call tasks (best score first), then the rest due today
  const hotIds = new Set(hot.map((p) => p.id));
  const taskBy = new Map((ot.calls || []).map((c: any) => [c.prospect && c.prospect.id, c]));
  const replies: any[] = (ot.replies || []).filter((r: any) => r.prospect && !hotIds.has(r.prospect.id));
  const calls: any[] = (ot.calls || []).filter((c: any) => c.prospect && !hotIds.has(c.prospect.id));
  const callIds = new Set(calls.map((c) => c.prospect.id));
  const due: any[] = ((data && data.due) || []).filter((p: any) => !callIds.has(p.id));
  const callBtn = (p: any, big?: boolean) => { const t = telOf(p); return t ? <a className={"btn btn-primary " + (big ? "" : "btn-sm")} href={t}><Icon name="phone" size={big ? 16 : 13} />Call {p.phone_display || ""}</a> : <span className="faint" style={{ fontSize: 12 }}>no phone on file</span>; };
  const points = (p: any) => (p.talking_points && p.talking_points.length ? <div className="le-points"><span className="kicker">Talking points</span>{p.talking_points.map((t: string, i: number) => <div key={i}>• {t}</div>)}</div> : null);

  return (
    <div className="page fade-up le-page" style={{ maxWidth: 1100 }}>
      <div className="le-head" style={{ marginBottom: 10 }}>
        <h1 className="display" style={{ fontSize: 28, margin: 0 }}>Today</h1>
        <p className="muted" style={{ margin: "4px 0 0" }}>Call the ones looking at their site first — a call while they're looking is a warm call. Then answer replies, then the pitch's call tasks and the calls due today, best score first.</p>
      </div>
      <LeTabs route="leadengine-today" go={go} due={hot.length + replies.length + calls.length + due.length} />
      {err && <p style={{ color: "var(--red)", fontSize: 13 }}>{err}</p>}
      {!data ? <p className="muted" style={{ textAlign: "center", padding: 24 }}>Loading…</p> : hot.length === 0 && due.length === 0 && calls.length === 0 && replies.length === 0 ? (
        <div className="card pad"><EmptyState icon="phone" title="Nothing to call right now" sub="When a prospect opens their preview site they show up here at the top. Calls you schedule (Next step, or Call back) land here on their day." action={{ label: "Open the pipeline", icon: "target", onClick: () => go("leadengine") }} secondary={{ label: "Find prospects", onClick: () => go("leadengine-find") }} /></div>
      ) : (
        <>
          {hot.length > 0 && (
            <section style={{ marginBottom: 18 }}>
              <h2 className="le-h2"><span className="le-live-dot" aria-hidden /> Looking at their site now</h2>
              <div className="le-hot-list">
                {hot.map((p) => (
                  <div key={p.id} className="card le-hot-card">
                    <div className="row between" style={{ gap: 10, flexWrap: "wrap", alignItems: "flex-start" }}>
                      <div style={{ minWidth: 0 }}>
                        <button className="le-name-btn" onClick={() => go("prospect", { id: p.id })}>{p.business_name}</button>
                        <div className="muted" style={{ fontSize: 13 }}>{[place(p), p.site ? "opened " + leTimeAgo(p.site.last_viewed_at) + " · " + (p.site.views || 0) + " view" + (p.site.views === 1 ? "" : "s") : ""].filter(Boolean).join(" · ")}</div>
                      </div>
                      <LeScore score={p.score} big />
                    </div>
                    <div className="le-hot-call">{callBtn(p, true)}</div>
                    {points(p)}
                    {taskBy.get(p.id) && <LoCallScript c={taskBy.get(p.id)} open />}
                    <OutcomeButtons p={p} compact onLogged={() => reloadAll()} />
                  </div>
                ))}
              </div>
            </section>
          )}
          {replies.length > 0 && (
            <section style={{ marginBottom: 18 }}>
              <h2 className="le-h2">Replied — answer them <span className="faint" style={{ fontWeight: 500, fontSize: 14 }}>· {replies.length}</span></h2>
              <div className="le-due-list">{replies.map((r) => <LoReplyCard key={r.id} r={r} go={go} onDone={() => reloadAll()} />)}</div>
            </section>
          )}
          {calls.length > 0 && (
            <section style={{ marginBottom: 18 }}>
              <h2 className="le-h2">Pitch calls <span className="faint" style={{ fontWeight: 500, fontSize: 14 }}>· {calls.length}</span></h2>
              <div className="le-due-list">{calls.map((c) => <LoCallCard key={c.id} c={c} go={go} narrow={narrow} onLogged={() => reloadAll()} />)}</div>
            </section>
          )}
          {due.length > 0 && (
            <section>
              <h2 className="le-h2">Due today <span className="faint" style={{ fontWeight: 500, fontSize: 14 }}>· {due.length}</span></h2>
              <div className="le-due-list">
                {due.map((p) => (
                  <div key={p.id} className="card le-due">
                    <div className="le-due-main">
                      <LeScore score={p.score} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <button className="le-name-btn small" onClick={() => go("prospect", { id: p.id })}>{p.business_name}</button>
                        <div style={{ fontSize: 12.5 }}><span style={{ color: leOverdue(p.next_step_at) && Date.parse(p.next_step_at) < Date.now() - 86400000 ? "var(--red)" : "var(--fg-2)" }}>{p.next_step || "Follow up"}</span><span className="faint"> · {place(p)}{place(p) ? " · " : ""}<LeStageText stage={p.stage} /> · due {shortDate(p.next_step_at)}</span></div>
                        {!narrow && points(p)}
                      </div>
                      {callBtn(p)}
                    </div>
                    <OutcomeButtons p={p} compact onLogged={() => load()} />
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
const LeStageText = ({ stage }: any) => <>{leStageLabel(stage)}</>;

Object.assign(window, { LeadEngineHome, LeadEnginePage, ProspectPage, LeadFindPage, LeadTodayPage });

export { OutreachPage } from "./leadoutreach";
export {
  LeadEngineHome, LeadEnginePage, ProspectPage, LeadFindPage, LeadTodayPage,
  LeTabs, LeSection, LeModal, LeScore, LeStage, OutcomeButtons, leErrText,
  leAutoMap, leRowsFromMapping, leChunks, leScoreBand, leTimeAgo, leOverdue, leStageCounts, leStageLabel, leBreakdownLines, leParseFindQuery,
  leSortFound, leMergeSummary, leFindSummary, LE_SIGNALS,
  leIsMigrationError, leEventView, leSafeHref, leSafeImg, leNormHeader,
  leNormState, leNormBusinessName, leSplitName, leContactName, leDrySummary, leSourceDetail,
};
